// AISplitPane.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./aISplitPane.scss";

import {
  SplitPaneControl,
  TabPaneControl,
  ToastNotifications,
  FieldControl,
  ButtonControl,
  IconControl,
  Loading,
  DialogControl,
  DragOverlay
} from "spark-core-dx/components";

import { useGenericManager } from "spark-core-dx/hooks";
import AIChatControl from "./AIChatControlV1";
import { ItemServiceFactory } from "../../services/ItemServiceFactory";
import { SystemTypes } from "spark-core-dx/services";
import AIChatDetailsControl from "./AIChatDetailsControl";
import { sanitizeHtmlFragment } from "../../utilities/markupSanitizer";
import {
  CHAT_OUTPUT_KIND_PLAN,
  CHAT_OUTPUT_KIND_PREVIEW,
  CHAT_REVIEW_STATE_PENDING,
  isPendingReviewChatItem,
  resolveChatHistoryDisplayMode,
  resolveChatOutputKind,
  resolveChatReviewState
} from "./chatOutputClassification";
import {
  isPermissionSetOperation,
  isPreviewRefreshAvailable as resolvePreviewRefreshAvailability,
  isWarehouseQuestionOperation,
  resolvePermissionPreviewAdditions
} from "./aiReviewPreviewUtils";


const CREATING_SESSION_MESSAGE = "Creating a new session...";
const STARTING_REQUEST_MESSAGE = "Starting your request...";
const GENERIC_THINKING_MESSAGE = "Working on your request...";
const CHAT_PANE_COLLAPSE_DISABLED = "disabled";
const CHAT_PANE_COLLAPSE_AUTO = "auto";
const CHAT_PANE_COLLAPSE_CLOSED = "closed";
const CHAT_PANE_COLLAPSE_OPEN = "open";
const SESSION_MODE_DIRECT = "Direct";
const SESSION_MODE_AGENT = "Agent";
const SESSION_MODE_PLANNING = "Planning";
const SESSION_MODE_PREVIEW = "Preview";
const AI_PROCESSING_MODE_CHOOSE = "Choose";
const AI_PROCESSING_MODE_DIRECT = "Direct";
const AI_PROCESSING_MODE_AGENT = "Agent";
const APPROVAL_AUTOMATIC = "Automatic";
const APPROVAL_APPROVE_FIRST = "Approve First";
const APPROVAL_READ_ONLY = "Read Only";
const SPEED_FAST = "Fast";
const SPEED_BALANCED = "Balanced";
const SPEED_THOROUGH = "Thorough";
const PROFILE_KEY_DEFAULT = "default";
const PROFILE_KEY_FAST = "fast";
const PROFILE_KEY_BALANCED = "balanced";
const PROFILE_KEY_THOROUGH = "thorough";
const LEGACY_PROFILE_KEY_STANDARD = "standard";
const LEGACY_PROFILE_KEY_DEV = "dev";
const SEEDED_PROFILE_ID_DEFAULT = "df7e4b63-9fa2-4aa0-8b67-1df97ce975d2";
const SEEDED_PROFILE_ID_FAST = "4be2744d-06e1-4a54-af4a-6246515f8969";
const SEEDED_PROFILE_ID_BALANCED = "cb4bd8dc-0e06-45b1-a4fb-d7e1d0c77b63";
const SEEDED_PROFILE_ID_THOROUGH = "7f298ac2-7c08-4a0c-84ca-998eaf8e4d7f";
const DIRECT_WAREHOUSE_AGENT_MODE_HINT_MESSAGE =
  "Direct mode could not build this warehouse result. Switch to Agent mode for multi-query results, reports, charts, or combined tables, then try again. In Direct mode, ask for one table, chart, or query at a time.";
const GENERIC_EMPTY_RESPONSE_MESSAGE =
  "Model did not respond with a valid value. Please contact support if this issue continues.";
const FILTER_UI_COMMAND = "filter";

const normalizeAIProcessingMode = (value, directModeOnly = false) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "direct" || normalized === "directonly" || normalized === "direct-only") {
    return AI_PROCESSING_MODE_DIRECT;
  }
  if (normalized === "agent") {
    return AI_PROCESSING_MODE_AGENT;
  }
  return directModeOnly === true ? AI_PROCESSING_MODE_DIRECT : AI_PROCESSING_MODE_CHOOSE;
};

const getProcessingModePresetSettings = (processingMode) => {
  if (processingMode === AI_PROCESSING_MODE_DIRECT) {
    return {
      OrchestrationMode: "Direct",
      ApprovalPolicyOverride: "AutoSafe",
      AILevelProfileKey: PROFILE_KEY_BALANCED
    };
  }

  if (processingMode === AI_PROCESSING_MODE_AGENT) {
    return {
      OrchestrationMode: "AgentAssist",
      ApprovalPolicyOverride: "AutoSafe",
      AILevelProfileKey: PROFILE_KEY_THOROUGH
    };
  }

  return null;
};

const hasSessionIdentifier = (session) =>
  String(
    session?._id ??
      session?.id ??
      session?.Id ??
      session?.AIChatSessionId ??
      session?.aiChatSessionId ??
      ""
  ).trim().length > 0;

const resolveEffectiveChatPaneCollapseMode = ({
  chatEnabled,
  isChatAiEnabled,
  chatPaneCollapseMode,
  chatPaneStartOpen,
  hasAvailableSession
}) => {
  if (!chatEnabled || !isChatAiEnabled) return CHAT_PANE_COLLAPSE_DISABLED;

  const requestedMode = String(chatPaneCollapseMode ?? CHAT_PANE_COLLAPSE_AUTO).trim().toLowerCase();
  if (requestedMode === CHAT_PANE_COLLAPSE_DISABLED) return CHAT_PANE_COLLAPSE_DISABLED;

  const resolvedMode =
    typeof chatPaneStartOpen === "boolean"
      ? chatPaneStartOpen
        ? CHAT_PANE_COLLAPSE_OPEN
        : CHAT_PANE_COLLAPSE_CLOSED
      : requestedMode === CHAT_PANE_COLLAPSE_AUTO
      ? hasAvailableSession
        ? CHAT_PANE_COLLAPSE_OPEN
        : CHAT_PANE_COLLAPSE_CLOSED
      : requestedMode;

  return resolvedMode;
};

const CONTROL_GROUPS = [
  {
    key: "mode",
    ariaLabel: "Mode",
    options: [SESSION_MODE_DIRECT, SESSION_MODE_AGENT]
  },
  {
    key: "approval",
    ariaLabel: "Approval",
    options: [APPROVAL_AUTOMATIC, APPROVAL_APPROVE_FIRST, APPROVAL_READ_ONLY]
  },
  {
    key: "speed",
    ariaLabel: "Speed",
    options: [SPEED_FAST, SPEED_BALANCED, SPEED_THOROUGH]
  }
];

function hasTextValue(value) {
  return String(value ?? "").trim().length > 0;
}

function isFailedOutcomeStatus(value) {
  return String(value ?? "").trim().toLowerCase() === "failed";
}

function isPlaceholderThinkingMessage(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return true;
  return /^thinking\.{0,3}$/i.test(normalized) || /^complete$/i.test(normalized);
}

const normalizeStoredMode = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized || normalized === "chat" || normalized === "direct") {
    return "Direct";
  }
  if (normalized === "agent assist") return "AgentAssist";
  if (normalized === "agentauto") return "AgentAuto";
  if (normalized === "agent auto") return "AgentAuto";
  if (normalized === "agentassist") return "AgentAssist";
  if (normalized === "plan" || normalized === "planning") return "Planning";
  if (normalized === "preview") return "Preview";
  return "Direct";
};

const mapStoredModeToUi = (value) => {
  const normalized = normalizeStoredMode(value);
  if (normalized === "Planning") return SESSION_MODE_PLANNING;
  if (normalized === "Preview") return SESSION_MODE_PREVIEW;
  return normalized === "Direct" ? SESSION_MODE_DIRECT : SESSION_MODE_AGENT;
};

const mapUiModeToStored = (value) => {
  if (value === SESSION_MODE_AGENT) return "AgentAssist";
  if (value === SESSION_MODE_PLANNING) return "Planning";
  if (value === SESSION_MODE_PREVIEW) return "Preview";
  return "Direct";
};

const mapStoredApprovalToUi = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "approvalrequired") return APPROVAL_APPROVE_FIRST;
  if (normalized === "readonly") return APPROVAL_READ_ONLY;
  return APPROVAL_AUTOMATIC;
};

const mapUiApprovalToStored = (value) => {
  if (value === APPROVAL_APPROVE_FIRST) return "ApprovalRequired";
  if (value === APPROVAL_READ_ONLY) return "ReadOnly";
  return "AutoSafe";
};

const normalizeStoredProfileKey = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized || normalized === LEGACY_PROFILE_KEY_STANDARD) return PROFILE_KEY_DEFAULT;
  if (normalized === LEGACY_PROFILE_KEY_DEV) return PROFILE_KEY_BALANCED;
  if (normalized === PROFILE_KEY_FAST) return PROFILE_KEY_FAST;
  if (normalized === PROFILE_KEY_BALANCED) return PROFILE_KEY_BALANCED;
  if (normalized === PROFILE_KEY_THOROUGH) return PROFILE_KEY_THOROUGH;
  return PROFILE_KEY_DEFAULT;
};

const mapProfileKeyToUi = (value) => {
  const normalized = normalizeStoredProfileKey(value);
  if (normalized === PROFILE_KEY_FAST) return SPEED_FAST;
  if (normalized === PROFILE_KEY_THOROUGH) return SPEED_THOROUGH;
  return SPEED_BALANCED;
};

const mapUiProfileToStoredKey = (value) => {
  if (value === SPEED_FAST) return PROFILE_KEY_FAST;
  if (value === SPEED_THOROUGH) return PROFILE_KEY_THOROUGH;
  return PROFILE_KEY_BALANCED;
};

const resolveLegacyProfileKeyFromId = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === SEEDED_PROFILE_ID_DEFAULT) return PROFILE_KEY_DEFAULT;
  if (normalized === SEEDED_PROFILE_ID_FAST) return PROFILE_KEY_FAST;
  if (normalized === SEEDED_PROFILE_ID_BALANCED) return PROFILE_KEY_BALANCED;
  if (normalized === SEEDED_PROFILE_ID_THOROUGH) return PROFILE_KEY_THOROUGH;
  return "";
};

const resolveStoredProfileKey = ({ profileKey, profileId, enableToolCalling, maxAgentSteps, orchestrationMode, forceDirectMode = false }) => {
  const explicitKey = normalizeStoredProfileKey(profileKey);
  if (String(profileKey ?? "").trim() && explicitKey !== PROFILE_KEY_DEFAULT) return explicitKey;

  const profileIdKey = resolveLegacyProfileKeyFromId(profileId);
  if (profileIdKey && profileIdKey !== PROFILE_KEY_DEFAULT) return profileIdKey;

  if (enableToolCalling === false) return PROFILE_KEY_FAST;
  if (Number(maxAgentSteps ?? 0) > 18) return PROFILE_KEY_THOROUGH;
  return PROFILE_KEY_BALANCED;
};

const buildFriendlySessionState = ({ orchestrationMode, approvalPolicyOverride, aiLevelProfileKey, enableToolCalling, maxAgentSteps, profileId, forceDirectMode = false }) => ({
  mode: mapStoredModeToUi(orchestrationMode),
  approval: mapStoredApprovalToUi(approvalPolicyOverride),
  speed: mapProfileKeyToUi(
    resolveStoredProfileKey({
      profileKey: aiLevelProfileKey,
      profileId,
      enableToolCalling,
      maxAgentSteps,
      orchestrationMode,
      forceDirectMode
    })
  )
});

const mapFriendlyModeToControlMode = (value) =>
  value === SESSION_MODE_PLANNING || value === SESSION_MODE_PREVIEW
    ? SESSION_MODE_AGENT
    : value;

const parseUtcDateValue = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const hasExplicitZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const normalized = hasExplicitZone ? raw : `${raw}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatSessionOptionTimePrefix = (dateValue, nowMs = Date.now()) => {
  const date = parseUtcDateValue(dateValue);
  if (!date) return "";

  const elapsedMs = Math.max(0, nowMs - date.getTime());
  const elapsedMinutes = Math.floor(elapsedMs / 60000);

  if (elapsedMinutes < 1) return "just now";
  if (elapsedMinutes < 60) return `${elapsedMinutes} min ago`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 6) {
    return elapsedHours === 1 ? "1 hour ago" : `${elapsedHours} hours ago`;
  }

  return date
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .replace(/\s+/g, "")
    .toLowerCase();
};

const getSessionOptionDateValue = (item) =>
  item?.ModifiedDate ??
  item?.modifiedDate ??
  item?.UpdatedDate ??
  item?.updatedDate ??
  item?.CreatedDate ??
  item?.createdDate ??
  null;

const REVIEW_TYPE_PLAN = "plan";
const REVIEW_TYPE_PREVIEW = "preview";

const parseMaybeJsonValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const stringifyReviewPayload = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const resolveChatResultValue = (chatResult) =>
  parseMaybeJsonValue(
    chatResult?.Value ??
      chatResult?.value ??
      chatResult?.Output ??
      chatResult?.output ??
      null
  );

const getObjectPropCI = (obj, ...names) => {
  if (!obj || typeof obj !== "object") return undefined;
  const lookup = new Map(Object.keys(obj).map((key) => [key.toLowerCase(), key]));
  for (const name of names) {
    const key = lookup.get(String(name).toLowerCase());
    if (key !== undefined) return obj[key];
  }
  return undefined;
};

const resolveUiActionArgsPayload = (value) => {
  const parsed = parseMaybeJsonValue(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return parsed;

  const nestedPayload = getObjectPropCI(
    parsed,
    "output",
    "Output",
    "value",
    "Value",
    "payload",
    "Payload",
    "data",
    "Data",
    "query",
    "Query",
    "generatedQuery",
    "GeneratedQuery"
  );

  return nestedPayload === undefined
    ? parsed
    : parseMaybeJsonValue(nestedPayload);
};

const asTextList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry ?? "").trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const normalizePreviewUiCommandValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "display") return "data";
  return normalized;
};

const isRenderablePreviewPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;

  const finalUiCommand = normalizePreviewUiCommandValue(
    getObjectPropCI(payload, "finalUiCommand", "FinalUiCommand")
  );
  if (!["data", "render", "chart", "update"].includes(finalUiCommand)) return false;

  const panelValue =
    getObjectPropCI(payload, "payload", "Payload", "output", "Output", "data", "Data") ??
    null;

  return panelValue != null;
};

const resolvePreviewPayloadMessage = (payload) => {
  if (!payload || typeof payload !== "object") return "";

  const args = getObjectPropCI(payload, "args", "Args");
  return String(
    getObjectPropCI(payload, "message", "Message") ??
      getObjectPropCI(args, "message", "Message", "comment", "Comment") ??
      ""
  ).trim();
};

const resolvePreviewRenderHtml = (payload) => {
  const parsed = parseMaybeJsonValue(payload);
  if (typeof parsed === "string") return parsed.trim();
  if (!parsed || typeof parsed !== "object") return "";

  const direct = String(
    getObjectPropCI(
      parsed,
      "html",
      "Html",
      "artifactHtml",
      "ArtifactHtml",
      "renderHtml",
      "RenderHtml"
    ) ?? ""
  ).trim();
  if (direct) return direct;

  const nested =
    getObjectPropCI(parsed, "payload", "Payload", "output", "Output", "data", "Data") ?? null;
  if (nested == null || nested === payload) return "";

  return resolvePreviewRenderHtml(nested);
};

const resolveReviewPreviewTarget = (reviewDraft) => {
  if (!reviewDraft || reviewDraft.kind !== REVIEW_TYPE_PREVIEW) return null;

  const finalUiCommand = normalizePreviewUiCommandValue(
    reviewDraft.finalUiCommand
  );
  if (!["data", "render", "chart", "update"].includes(finalUiCommand)) return null;

  const previewPayload =
    reviewDraft.payload && typeof reviewDraft.payload === "object"
      ? reviewDraft.payload
      : null;
  if (!previewPayload) return null;

  const panelValue =
    getObjectPropCI(
      previewPayload,
      "payload",
      "Payload",
      "output",
      "Output",
      "data",
      "Data"
    ) ?? null;

  if (panelValue == null) return null;

  return {
    kind: finalUiCommand,
    panelCommand: finalUiCommand,
    panelValue
  };
};

const hasMeaningfulReviewValue = (value) => {
  if (Array.isArray(value)) {
    return value.some((entry) => String(entry ?? "").trim().length > 0);
  }
  if (value && typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return String(value ?? "").trim().length > 0;
};

const looksLikePlanPayload = (value) => {
  const payload = parseMaybeJsonValue(value);
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return false;

  const planFields = [
    ["requestedGoal", "RequestedGoal", "goal", "Goal"],
    ["targetPanel", "TargetPanel", "targetOperationName", "TargetOperationName"],
    ["proposedActions", "ProposedActions", "plannedActions", "PlannedActions"],
    ["dataNeeds", "DataNeeds", "datasets", "Datasets", "queryGoals", "QueryGoals"],
    ["expectedOutput", "ExpectedOutput"],
    ["components", "Components", "outputComponents", "OutputComponents"],
    ["assumptions", "Assumptions"],
    ["openQuestions", "OpenQuestions"],
    ["constraints", "Constraints"]
  ];

  return planFields.some((fieldNames) =>
    hasMeaningfulReviewValue(getObjectPropCI(payload, ...fieldNames))
  );
};

const hasMeaningfulReviewDraft = (draft) => {
  if (!draft) return false;

  if (draft.kind === REVIEW_TYPE_PLAN) {
    return (
      String(draft.requestedGoal ?? "").trim().length > 0 ||
      String(draft.targetPanel ?? "").trim().length > 0 ||
      String(draft.expectedOutput ?? "").trim().length > 0 ||
      asTextList(draft.proposedActions).length > 0 ||
      asTextList(draft.dataNeeds).length > 0 ||
      asTextList(draft.components).length > 0 ||
      asTextList(draft.assumptions).length > 0 ||
      asTextList(draft.openQuestions).length > 0 ||
      asTextList(draft.constraints).length > 0
    );
  }

  if (draft.kind === REVIEW_TYPE_PREVIEW) {
    return isRenderablePreviewPayload(draft.payload);
  }

  return true;
};

const isFinalReviewAction = (action) => {
  const normalized = String(action ?? "").trim().toLowerCase();
  return normalized === "generateresult" ||
    normalized === "approve" ||
    normalized === "approveresult" ||
    normalized === "generate" ||
    normalized === "acceptpreview";
};

const extractReviewPayload = (kind, chatResult, item0) => {
  const value = resolveChatResultValue(chatResult);
  const topLevelOutput = parseMaybeJsonValue(chatResult?.Output ?? chatResult?.output ?? null);
  const output = parseMaybeJsonValue(item0?.Output ?? item0?.output ?? item0?.ModelResponse ?? item0?.modelResponse);
  const candidates = [value, topLevelOutput, output, chatResult, item0].filter((entry) => entry != null);

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const direct = getObjectPropCI(candidate, kind);
    if (direct) return parseMaybeJsonValue(direct);
    const args = getObjectPropCI(candidate, "args", "Args");
    const fromArgs = getObjectPropCI(args, kind);
    if (fromArgs) return parseMaybeJsonValue(fromArgs);
    const metadata = getObjectPropCI(candidate, "metadata", "Metadata");
    const fromMetadata = getObjectPropCI(metadata, kind);
    if (fromMetadata) return parseMaybeJsonValue(fromMetadata);
    if (kind === REVIEW_TYPE_PLAN && looksLikePlanPayload(candidate)) {
      return candidate;
    }
  }

  if (kind === REVIEW_TYPE_PLAN) {
    const parsedValue = parseMaybeJsonValue(value);
    return looksLikePlanPayload(parsedValue) ? parsedValue : null;
  }

  return value;
};

const extractEmbeddedUiAction = (chatResult, item0) => {
  const chatMessageCandidates = [chatResult, item0].flatMap((source) => {
    const messages = getObjectPropCI(source, "chatMessages", "ChatMessages");
    if (!Array.isArray(messages)) return [];

    return messages
      .slice()
      .sort((left, right) => (right?.Sequence ?? 0) - (left?.Sequence ?? 0))
      .flatMap((message) => [
        message?.Output,
        message?.output,
        message?.ResponseMessage,
        message?.responseMessage,
        message
      ]);
  });
  const candidates = [
    ...chatMessageCandidates,
    chatResult?.Value,
    chatResult?.Output,
    chatResult?.output,
    chatResult?.ModelResponse,
    chatResult?.modelResponse,
    chatResult?.ResponseMessage,
    chatResult?.responseMessage,
    item0?.Output,
    item0?.output,
    item0?.ModelResponse,
    item0?.modelResponse,
    item0?.ResponseMessage,
    item0?.responseMessage,
    chatResult,
    item0
  ]
    .map((entry) => parseMaybeJsonValue(entry))
    .filter((entry) => entry && typeof entry === "object");

  for (const candidate of candidates) {
    const uiCommand = getObjectPropCI(candidate, "uiCommand", "UICommand", "UiCommand");
    if (!uiCommand) continue;

    const args =
      parseMaybeJsonValue(getObjectPropCI(candidate, "args", "Args")) ??
      candidate;

    const comment =
      getObjectPropCI(args, "comment", "Comment", "message", "Message") ??
      getObjectPropCI(candidate, "comment", "Comment", "message", "Message") ??
      "";

    return {
      uiAction: String(uiCommand).trim(),
      args,
      comment: String(comment ?? "").trim()
    };
  }

  return null;
};

const hasObjectPropCI = (obj, ...names) => getObjectPropCI(obj, ...names) !== undefined;

const looksLikeFilterQueryPayload = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return hasObjectPropCI(
    value,
    "SourceObjectId",
    "sourceObjectId",
    "EntityObjectId",
    "entityObjectId",
    "RootFilterNode",
    "rootFilterNode",
    "SelectColumns",
    "selectColumns",
    "Skip",
    "skip",
    "Take",
    "take",
    "SortBy",
    "sortBy"
  );
};

const resolveFilterQueryPayload = (payload, seen = new Set()) => {
  const parsed = parseMaybeJsonValue(payload);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  if (seen.has(parsed)) return null;
  seen.add(parsed);

  if (looksLikeFilterQueryPayload(parsed)) return parsed;

  const args = getObjectPropCI(parsed, "args", "Args");
  const metadata = getObjectPropCI(parsed, "metadata", "Metadata");
  const candidates = [
    getObjectPropCI(metadata, "query", "Query"),
    getObjectPropCI(args, "query", "Query"),
    getObjectPropCI(args, "output", "Output"),
    getObjectPropCI(args, "value", "Value"),
    getObjectPropCI(parsed, "query", "Query"),
    getObjectPropCI(parsed, "generatedQuery", "GeneratedQuery"),
    getObjectPropCI(parsed, "value", "Value"),
    getObjectPropCI(parsed, "output", "Output")
  ];

  for (const candidate of candidates) {
    const resolved = resolveFilterQueryPayload(candidate, seen);
    if (resolved) return resolved;
  }

  return null;
};

const resolveFilterPanelValue = (chatResult, item0 = null, valueFallback = null) => {
  const candidates = [
    valueFallback,
    resolveChatResultValue(chatResult),
    chatResult?.GeneratedQuery,
    chatResult?.generatedQuery,
    item0?.GeneratedQuery,
    item0?.generatedQuery,
    chatResult?.Value,
    chatResult?.value,
    item0?.Value,
    item0?.value,
    chatResult?.Output,
    chatResult?.output,
    item0?.Output,
    item0?.output,
    chatResult,
    item0
  ];

  for (const candidate of candidates) {
    const resolved = resolveFilterQueryPayload(candidate);
    if (resolved) return resolved;
  }

  return null;
};

const resolveUiCommandValue = (value) =>
  String(getObjectPropCI(value, "UICommand", "uiCommand", "UiCommand") ?? "").trim();

const isFilterUiCommand = (value) =>
  String(value ?? "").trim().toLowerCase() === FILTER_UI_COMMAND;

const resolveDirectUiAction = (chatResult) => {
  const direct = String(
    chatResult?.UICommand ?? chatResult?.uiCommand ?? chatResult?.UiCommand ?? ""
  ).trim();
  if (direct && direct.toLowerCase() !== "none") {
    return direct;
  }

  const parsedOutput = parseMaybeJsonValue(chatResult?.Output ?? chatResult?.output ?? null);
  if (!parsedOutput || typeof parsedOutput !== "object") {
    return "";
  }

  const parsedCommand = String(
    getObjectPropCI(parsedOutput, "uiCommand", "UICommand", "UiCommand") ?? ""
  ).trim();
  return parsedCommand && parsedCommand.toLowerCase() !== "none" ? parsedCommand : "";
};

const extractReviewDraftFromChatItem = (chatItem) => {
  if (!chatItem || typeof chatItem !== "object") return null;

  const embeddedUiAction = extractEmbeddedUiAction(
    {
      Value: chatItem?.Value ?? chatItem?.value ?? null,
      ResponseMessage: chatItem?.ResponseMessage ?? chatItem?.responseMessage ?? null
    },
    chatItem
  );
  const directUiAction = resolveDirectUiAction(chatItem);

  const outputKind = resolveChatOutputKind(
    chatItem,
    embeddedUiAction?.uiAction ?? directUiAction ?? ""
  );
  if (outputKind === CHAT_OUTPUT_KIND_PLAN) {
    return createReviewDraft(
      REVIEW_TYPE_PLAN,
      extractReviewPayload(
        REVIEW_TYPE_PLAN,
        {
          Value: chatItem?.Value ?? chatItem?.value ?? null,
          Output: chatItem?.Output ?? chatItem?.output ?? null,
          ResponseMessage: chatItem?.ResponseMessage ?? chatItem?.responseMessage ?? null,
          ModelResponse: chatItem?.ModelResponse ?? chatItem?.modelResponse ?? null
        },
        chatItem
      )
    );
  }

  if (outputKind === CHAT_OUTPUT_KIND_PREVIEW) {
    const previewPayload = extractReviewPayload(
      REVIEW_TYPE_PREVIEW,
      {
        Value: chatItem?.Value ?? chatItem?.value ?? null,
        Output: chatItem?.Output ?? chatItem?.output ?? null,
        ResponseMessage: chatItem?.ResponseMessage ?? chatItem?.responseMessage ?? null,
        ModelResponse: chatItem?.ModelResponse ?? chatItem?.modelResponse ?? null
      },
      chatItem
    );
    if (!isRenderablePreviewPayload(previewPayload)) {
      return null;
    }

    return createReviewDraft(
      REVIEW_TYPE_PREVIEW,
      previewPayload
    );
  }

  return null;
};

const createReviewDraft = (kind, payload) => {
  const body = payload && typeof payload === "object" ? payload : { message: String(payload ?? "").trim() };
  const plan = kind === REVIEW_TYPE_PLAN ? body : null;
  const preview = kind === REVIEW_TYPE_PREVIEW ? body : null;

  return {
    kind,
    draftId: getObjectPropCI(body, "draftId", "DraftId") ?? null,
    status: getObjectPropCI(body, "status", "Status") ?? (kind === REVIEW_TYPE_PLAN ? "Draft" : "Preview"),
    requestedGoal:
      getObjectPropCI(body, "requestedGoal", "RequestedGoal", "goal", "Goal") ??
      getObjectPropCI(plan, "requestedGoal", "RequestedGoal") ??
      "",
    targetPanel:
      getObjectPropCI(body, "targetPanel", "TargetPanel", "targetOperationName", "TargetOperationName") ??
      "",
    proposedActions: asTextList(getObjectPropCI(body, "proposedActions", "ProposedActions", "plannedActions", "PlannedActions")),
    dataNeeds: asTextList(getObjectPropCI(body, "dataNeeds", "DataNeeds", "datasets", "Datasets", "queryGoals", "QueryGoals")),
    expectedOutput:
      kind === REVIEW_TYPE_PLAN
        ? getObjectPropCI(body, "expectedOutput", "ExpectedOutput") ?? ""
        : "",
    components: asTextList(getObjectPropCI(body, "components", "Components", "outputComponents", "OutputComponents")),
    assumptions: asTextList(getObjectPropCI(body, "assumptions", "Assumptions")),
    openQuestions: asTextList(getObjectPropCI(body, "openQuestions", "OpenQuestions")),
    constraints: asTextList(getObjectPropCI(body, "constraints", "Constraints")),
    finalUiCommand: getObjectPropCI(preview, "finalUiCommand", "FinalUiCommand") ?? null,
    payload: body
  };
};

const extractSessionReviewDrafts = (sessionItem) => {
  const emptyDrafts = { plan: null, preview: null };
  if (!sessionItem || typeof sessionItem !== "object") return emptyDrafts;

  const customFields = getObjectPropCI(sessionItem, "CustomFields", "customFields") ?? {};
  const rawPlan =
    getObjectPropCI(sessionItem, "PendingPlanningDraft", "pendingPlanningDraft") ??
    getObjectPropCI(customFields, "pending_planning_draft");
  const rawPreview =
    getObjectPropCI(sessionItem, "PendingPreviewDraft", "pendingPreviewDraft") ??
    getObjectPropCI(customFields, "pending_preview_draft");

  const planDraft = parseMaybeJsonValue(rawPlan);
  const previewDraft = parseMaybeJsonValue(rawPreview);

  const result = { ...emptyDrafts };

  if (planDraft && typeof planDraft === "object") {
    const planPayload =
      parseMaybeJsonValue(getObjectPropCI(planDraft, "PlanJson", "planJson")) ?? planDraft;
    result.plan = {
      ...createReviewDraft(REVIEW_TYPE_PLAN, planPayload),
      draftId: getObjectPropCI(planDraft, "DraftId", "draftId") ?? null,
      status: getObjectPropCI(planDraft, "Status", "status") ?? "Draft"
    };
  }

  if (previewDraft && typeof previewDraft === "object") {
    const previewPayload = {
      ...previewDraft,
      finalUiCommand: getObjectPropCI(previewDraft, "FinalUiCommand", "finalUiCommand")
    };
    if (isRenderablePreviewPayload(previewPayload)) {
      result.preview = {
        ...createReviewDraft(REVIEW_TYPE_PREVIEW, previewPayload),
        draftId: getObjectPropCI(previewDraft, "DraftId", "draftId") ?? null
      };
    }
  }

  return result;
};

const extractSessionReviewDraft = (sessionItem) => {
  const drafts = extractSessionReviewDrafts(sessionItem);
  return drafts.preview ?? drafts.plan ?? null;
};

const collectReviewDraftsFromChats = (items) =>
  (Array.isArray(items) ? items : [])
    .slice()
    .sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0))
    .reduce(
      (acc, item) => {
        if (!isPendingReviewChatItem(item)) return acc;
        const draft = extractReviewDraftFromChatItem(item);
        if (draft?.kind === REVIEW_TYPE_PLAN) acc.plan = draft;
        if (draft?.kind === REVIEW_TYPE_PREVIEW) acc.preview = draft;
        return acc;
      },
      { plan: null, preview: null }
    );

export const AISplitPane = ({
  className,
  children,
  onUpdatePanel,
  onBeforeSend,
  onSessionChange,
  onOpenAssociatedOutput,
  session,                    // if provided and has _id, we will load & start this session (no autostart)
  aiChats = null,             // optional preloaded AIChat items to avoid per-session Query calls
  referenceId,
  referenceName,
  params,
  operationName,
  enableContentDragHiding = true,
  chatEnabled = true,
  chatPaneToggleEnabled = true,
  startChat = null,           // if string (non-empty): auto-start chat and send this as the first message (only when no session id)
  chatPane = "right",         // 'right' | 'middle' | 'left'
  chatPaneCollapseMode = CHAT_PANE_COLLAPSE_AUTO, // 'auto' | 'disabled' | 'open' | 'closed'
  chatPaneStartOpen = null,    // boolean override for auto start state
  directModeOnly = false,
  aiProcessingMode = null,
  defaultOrchestrationMode = null,
  singleSessionMode = false,
  planningPreviewEnabled = true,
  previewPanelEnabled = false,
  previewRefreshEnabled = null,
  payload = null,
  reviewContext = null,
  // pane inputs can be React nodes or tabs [{ key, title|label, content }]
  left: leftInput,
  middle: middleInput,
  right: rightInput,

  // SplitPane sizing
  storageKey = "ai-panel-left-width",
  initialRightPct = 0.33,
  initialLeftPct = 0.67,
  minLeft = 360,
  minMiddle = 360,
  minRight = 360,
  splashMessage = null,
  introMessage = null,
  bottomDrawerEnabled = false,
  bottomDrawerDefaultOpen = false,
  bottomDrawerTitle = "Debug",
  bottomDrawerContent = null
}) => {
  const resolvedAIProcessingMode = normalizeAIProcessingMode(aiProcessingMode, directModeOnly);
  const processingModePresetSettings = useMemo(
    () => getProcessingModePresetSettings(resolvedAIProcessingMode),
    [resolvedAIProcessingMode]
  );
  const hasFixedProcessingMode = processingModePresetSettings != null;
  const forceDirectMode = resolvedAIProcessingMode === AI_PROCESSING_MODE_DIRECT;
  const forceSingleSessionMode = singleSessionMode === true;
  const isChatAiEnabled = chatEnabled && String(operationName ?? "").trim().length > 0;
  const initialEffectiveChatPaneCollapseMode = resolveEffectiveChatPaneCollapseMode({
    chatEnabled,
    isChatAiEnabled,
    chatPaneCollapseMode,
    chatPaneStartOpen,
    hasAvailableSession: hasSessionIdentifier(session)
  });

  // ---- UI State ----
  const [uiMode, setUiMode] = useState("startup"); // 'startup' | 'chat'
  const [isLoadingHistory, setIsLoadingHistory] = useState(false); // gate visual switch until history finishes
  const [toasterMessage, setToasterMessage] = useState(null);
  const [sessionChatMessages, setSessionChatMessages] = useState(null); // null = empty, undefined = loading, array = loaded
  const [chatSession, setChatSession] = useState(null);
  const [thinking, setThinking] = useState("");
  const [chatApi, setChatApi] = useState();
  const [chatInstanceKey, setChatInstanceKey] = useState(0);
  const [orchestrationMode, setOrchestrationMode] = useState(
    processingModePresetSettings?.OrchestrationMode ?? normalizeStoredMode(session?.OrchestrationMode ?? defaultOrchestrationMode)
  );
  const orchestrationModeRef = useRef(orchestrationMode);
  const [approvalPolicyOverride, setApprovalPolicyOverride] = useState(processingModePresetSettings?.ApprovalPolicyOverride ?? session?.ApprovalPolicyOverride ?? "AutoSafe");
  const [aiLevelProfileKey, setAiLevelProfileKey] = useState(
    processingModePresetSettings?.AILevelProfileKey ??
      resolveStoredProfileKey({
        profileKey: session?.AILevelProfileKey,
        profileId: session?.AILevelProfileId,
        enableToolCalling: session?.EnableToolCalling === false ? false : true,
        maxAgentSteps: Number(session?.MaxAgentSteps ?? 18) > 0 ? Number(session?.MaxAgentSteps ?? 18) : 18,
        orchestrationMode: session?.OrchestrationMode,
        forceDirectMode
      })
  );
  const [enableToolCalling, setEnableToolCalling] = useState(
    session?.EnableToolCalling === false ? false : true
  );
  const [maxAgentSteps, setMaxAgentSteps] = useState(
    Number(session?.MaxAgentSteps ?? 18) > 0 ? Number(session?.MaxAgentSteps ?? 18) : 18
  );
  const [jsonData, setJsonData] = useState([]);
  const [allChats, setAllChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedChatDefaultTab, setSelectedChatDefaultTab] = useState("flow");
  const [localErrorDetail, setLocalErrorDetail] = useState(null);
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [activeChatMeta, setActiveChatMeta] = useState(null);
  const [approvalActionInFlight, setApprovalActionInFlight] = useState(null);
  const [activeReviewDraft, setActiveReviewDraft] = useState(null);
  const [reviewActionInFlight, setReviewActionInFlight] = useState(null);
  const [enablePreviewRefresh, setEnablePreviewRefresh] = useState(false);
  const [dragging, setDraging] = useState(false);
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(!!bottomDrawerDefaultOpen);
  const [isLatestTurnCollapsed, setIsLatestTurnCollapsed] = useState(true);
  const [isChatPaneCollapsed, setIsChatPaneCollapsed] = useState(
    chatEnabled &&
    chatPane === "right" &&
    initialEffectiveChatPaneCollapseMode === CHAT_PANE_COLLAPSE_CLOSED
  );
  const [splitPaneSize, setSplitPaneSize] = useState({ left: 0, middle: 0, right: 0, total: 0 });
  const effectiveChatPaneCollapseMode = resolveEffectiveChatPaneCollapseMode({
    chatEnabled,
    isChatAiEnabled,
    chatPaneCollapseMode,
    chatPaneStartOpen,
    hasAvailableSession: hasSessionIdentifier(session) || hasSessionIdentifier(chatSession)
  });


  // Controlled dropdown selection (do NOT derive from chatSession to avoid flicker)
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // Auto-start lifecycle
  const [pendingAutoPrompt, setPendingAutoPrompt] = useState(null);
  const [didAutoStart, setDidAutoStart] = useState(false);
  const autoStartInProgressRef = useRef(false);
  const initSeededRef = useRef(false);
  const [chatUiReady, setChatUiReady] = useState(false);

  // Guards
  const inFlightSendRef = useRef(false);
  const hasAutoStartedRef = useRef(null);
  const pendingReviewActionRef = useRef(null);
  const pendingSessionSettingsRef = useRef({});
  const pendingSessionSettingsSessionIdRef = useRef(null);
  const sessionSettingsSaveVersionRef = useRef(0);
  const deferredSessionSettingsRef = useRef(null);

  const setOrchestrationModeImmediate = useCallback(
    (nextMode) => {
      const normalizedMode = processingModePresetSettings?.OrchestrationMode ?? normalizeStoredMode(nextMode);
      orchestrationModeRef.current = normalizedMode;
      setOrchestrationMode(normalizedMode);
      return normalizedMode;
    },
    [processingModePresetSettings]
  );

  // Load dedupe & state
  const loadingSessionsRef = useRef(new Set()); // ids currently loading
  const lastLoadedIdRef = useRef(null);

  // Sticky "user chose a session here" flag; prevents parent !session from resetting UI
  const localPinnedRef = useRef(false);
  const ignoredSessionPropIdRef = useRef(null);

  const chatRef = useRef(null);

  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const aiSessionMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChatSession);
  const aiChatMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChat);
  const aiSessionMgrRef = useRef(aiSessionMgr);

  useEffect(() => {
    aiSessionMgrRef.current = aiSessionMgr;
  }, [aiSessionMgr]);

  useEffect(() => {
    if (!processingModePresetSettings) return;
    if (orchestrationMode !== processingModePresetSettings.OrchestrationMode) {
      setOrchestrationModeImmediate(processingModePresetSettings.OrchestrationMode);
    }
    if (approvalPolicyOverride !== processingModePresetSettings.ApprovalPolicyOverride) {
      setApprovalPolicyOverride(processingModePresetSettings.ApprovalPolicyOverride);
    }
    if (aiLevelProfileKey !== processingModePresetSettings.AILevelProfileKey) {
      setAiLevelProfileKey(processingModePresetSettings.AILevelProfileKey);
    }
  }, [aiLevelProfileKey, approvalPolicyOverride, orchestrationMode, processingModePresetSettings, setOrchestrationModeImmediate]);

  useEffect(() => {
    setEnablePreviewRefresh(false);
  }, [activeReviewDraft?.kind, activeReviewDraft?.draftId]);

  const applySessionSettings = useCallback((sessionItem) => {
    if (!sessionItem) return;
    const sessionId = String(sessionItem?._id ?? "");
    const pendingSettings =
      sessionId && pendingSessionSettingsSessionIdRef.current === sessionId
        ? pendingSessionSettingsRef.current
        : {};
    const effectiveSessionItem = { ...sessionItem, ...pendingSettings };
    const nextMode = processingModePresetSettings?.OrchestrationMode ??
      normalizeStoredMode(effectiveSessionItem?.OrchestrationMode ?? defaultOrchestrationMode);
    setOrchestrationModeImmediate(nextMode);
    setApprovalPolicyOverride(processingModePresetSettings?.ApprovalPolicyOverride ?? effectiveSessionItem?.ApprovalPolicyOverride ?? "AutoSafe");
    setEnableToolCalling(effectiveSessionItem?.EnableToolCalling === false ? false : true);
    const nextMaxSteps = Number(effectiveSessionItem?.MaxAgentSteps ?? 18);
    setMaxAgentSteps(Number.isFinite(nextMaxSteps) && nextMaxSteps > 0 ? nextMaxSteps : 18);
    setAiLevelProfileKey(
      processingModePresetSettings?.AILevelProfileKey ??
        resolveStoredProfileKey({
          profileKey: effectiveSessionItem?.AILevelProfileKey,
          profileId: effectiveSessionItem?.AILevelProfileId,
          enableToolCalling: effectiveSessionItem?.EnableToolCalling === false ? false : true,
          maxAgentSteps: nextMaxSteps,
          orchestrationMode: nextMode,
          forceDirectMode
        })
    );
    const sessionReviewDraft = extractSessionReviewDraft(effectiveSessionItem);
    setActiveReviewDraft(sessionReviewDraft ?? null);
  }, [defaultOrchestrationMode, forceDirectMode, processingModePresetSettings, setOrchestrationModeImmediate]);

  const buildSessionSettings = useCallback(() => {
    const effectiveOrchestrationMode = processingModePresetSettings?.OrchestrationMode ??
      normalizeStoredMode(orchestrationModeRef.current ?? orchestrationMode);

    return {
      OrchestrationMode: effectiveOrchestrationMode,
      ApprovalPolicyOverride: processingModePresetSettings?.ApprovalPolicyOverride ?? approvalPolicyOverride ?? "AutoSafe",
      AILevelProfileKey: processingModePresetSettings?.AILevelProfileKey ??
        resolveStoredProfileKey({
          profileKey: aiLevelProfileKey,
          profileId: chatSession?.AILevelProfileId ?? session?.AILevelProfileId,
          enableToolCalling,
          maxAgentSteps,
          orchestrationMode: effectiveOrchestrationMode,
          forceDirectMode
        })
    };
  }, [aiLevelProfileKey, approvalPolicyOverride, chatSession?.AILevelProfileId, enableToolCalling, forceDirectMode, maxAgentSteps, orchestrationMode, processingModePresetSettings, session?.AILevelProfileId]);

  const loadSessionSelectData = useCallback(async () => {
    if (!chatEnabled || !isChatAiEnabled) {
      setJsonData([]);
      return;
    }

    if (forceSingleSessionMode || hasFixedProcessingMode) {
      setJsonData([]);
      return;
    }

    const activeSessionMgr = aiSessionMgrRef.current;
    if (!activeSessionMgr) {
      setJsonData([]);
      return;
    }

    const parseSortValue = (value) => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const asTime = Date.parse(String(value ?? ""));
      return Number.isNaN(asTime) ? 0 : asTime;
    };

    const toSessionOption = (item) => {
      const label =
        String(item?.Name ?? "").trim() ||
        String(item?.Text ?? "").trim() ||
        String(item?.Summary ?? "").trim() ||
        "Session";
      const timePrefix = formatSessionOptionTimePrefix(getSessionOptionDateValue(item));

      return {
        Text: timePrefix ? `${timePrefix} - ${label}` : label,
        Value: item?._id ?? "",
        FilterValue: item?.ReferenceId ?? ""
      };
    };

    try {
      const referenceItemName = String(referenceName ?? "").trim();
      const result = await activeSessionMgr.Query({
        model: referenceId
          ? "x => x.IsActive == true && x.IsDeleted == false && x.ReferenceId == referenceId"
          : "x => x.IsActive == true && x.IsDeleted == false",
        params: referenceId ? { referenceId } : undefined
      });

      const allOptions = (Array.isArray(result?.Items) ? result.Items : [])
        .filter((item) => {
          const itemReferenceName = String(item?.ReferenceItemName ?? "").trim();
          return (
            (!referenceId || item?.ReferenceId === referenceId) &&
            (!referenceItemName || !itemReferenceName || itemReferenceName === referenceItemName)
          );
        })
        .sort((left, right) => {
          const rightSort =
            parseSortValue(getSessionOptionDateValue(right)) ||
            Number(right?.Sequence ?? 0);
          const leftSort =
            parseSortValue(getSessionOptionDateValue(left)) ||
            Number(left?.Sequence ?? 0);
          return rightSort - leftSort;
        })
        .map(toSessionOption)
        .filter((item) => String(item?.Value ?? "").trim() !== "");

      setJsonData(
        referenceId
          ? allOptions.filter((item) => item?.FilterValue === referenceId)
          : allOptions
      );
    } catch {
      setJsonData([]);
    }
  }, [chatEnabled, forceSingleSessionMode, hasFixedProcessingMode, isChatAiEnabled, referenceId, referenceName]);

  const friendlySessionState = useMemo(
    () =>
      buildFriendlySessionState({
        orchestrationMode,
        approvalPolicyOverride,
        aiLevelProfileKey,
        enableToolCalling,
        maxAgentSteps,
        profileId: chatSession?.AILevelProfileId ?? session?.AILevelProfileId,
        forceDirectMode
      }),
    [aiLevelProfileKey, approvalPolicyOverride, chatSession?.AILevelProfileId, enableToolCalling, forceDirectMode, maxAgentSteps, orchestrationMode, session?.AILevelProfileId]
  );

  const persistSessionSettings = useCallback(
    async (patch) => {
      const baseSession = chatSession ?? session ?? null;
      const baseSessionId = String(baseSession?._id ?? "");
      const nextPendingSettings = {
        ...(pendingSessionSettingsSessionIdRef.current === baseSessionId
          ? pendingSessionSettingsRef.current
          : {}),
        ...patch
      };
      pendingSessionSettingsSessionIdRef.current = baseSessionId || null;
      pendingSessionSettingsRef.current = nextPendingSettings;
      const saveVersion = sessionSettingsSaveVersionRef.current + 1;
      sessionSettingsSaveVersionRef.current = saveVersion;

      setChatSession((prev) => {
        const nextBase = prev ?? baseSession;
        return nextBase ? { ...nextBase, ...nextPendingSettings } : nextBase;
      });

      if (inFlightSendRef.current) {
        deferredSessionSettingsRef.current = {
          ...(deferredSessionSettingsRef.current ?? {}),
          ...patch
        };
        return;
      }

      if (!baseSession?._id) return;

      try {
        const saveResult = await aiSessionMgr.saveItem({
          ...baseSession,
          ...patch
        });
        const savedSession =
          saveResult?.Items?.first?.() ??
          first(saveResult?.Items) ??
          saveResult?.Items ??
          null;

        if (savedSession) {
          if (sessionSettingsSaveVersionRef.current !== saveVersion) return;

          const pendingSettings =
            pendingSessionSettingsSessionIdRef.current === baseSessionId
              ? pendingSessionSettingsRef.current
              : {};
          const mergedSession = {
            ...baseSession,
            ...savedSession,
            ...pendingSettings
          };
          setChatSession(mergedSession);
          applySessionSettings(mergedSession);
          if (typeof onSessionChange === "function") {
            onSessionChange(mergedSession._id, mergedSession, allChats);
          }
        }
      } catch {
        setToasterMessage("Could not update session settings.");
        window.setTimeout(() => setToasterMessage(null), 2400);
      } finally {
        if (
          sessionSettingsSaveVersionRef.current === saveVersion &&
          pendingSessionSettingsSessionIdRef.current === baseSessionId
        ) {
          pendingSessionSettingsRef.current = {};
          pendingSessionSettingsSessionIdRef.current = null;
        }
      }
    },
    [aiSessionMgr, allChats, applySessionSettings, chatSession, onSessionChange, session]
  );

  const refreshSessionState = useCallback(
    async (sessionId, chatsForSession = null) => {
      if (!sessionId) return null;

      try {
        const resp = await aiSessionMgr.getItem(sessionId);
        const savedSession =
          resp?.Items?.first?.() ??
          first(resp?.Items) ??
          resp?.Items ??
          null;

        if (!savedSession) return null;

        setChatSession(savedSession);
        applySessionSettings(savedSession);
        if (typeof onSessionChange === "function") {
          onSessionChange(savedSession._id, savedSession, chatsForSession ?? allChats);
        }
        void loadSessionSelectData();
        return savedSession;
      } catch {
        return null;
      }
    },
    [aiSessionMgr, allChats, applySessionSettings, loadSessionSelectData, onSessionChange]
  );

  const handleModeSelection = useCallback(
    (nextMode) => {
      if (forceDirectMode) {
        setOrchestrationModeImmediate(SESSION_MODE_DIRECT);
        return;
      }

      const nextStoredMode = mapUiModeToStored(nextMode);
      setOrchestrationModeImmediate(nextStoredMode);
      const nextPatch = { OrchestrationMode: nextStoredMode };
      void persistSessionSettings(nextPatch);
    },
    [forceDirectMode, persistSessionSettings, setOrchestrationModeImmediate]
  );

  const handleApprovalSelection = useCallback(
    (nextApproval) => {
      const nextStoredApproval = mapUiApprovalToStored(nextApproval);
      setApprovalPolicyOverride(nextStoredApproval);
      void persistSessionSettings({ ApprovalPolicyOverride: nextStoredApproval });
    },
    [persistSessionSettings]
  );

  const handleSpeedSelection = useCallback(
    (nextSpeed) => {
      const nextProfileKey = mapUiProfileToStoredKey(nextSpeed);
      setAiLevelProfileKey(nextProfileKey);
      void persistSessionSettings({ AILevelProfileKey: nextProfileKey });
    },
    [persistSessionSettings]
  );

  const handlePlanningSelection = useCallback(() => {
    if (forceDirectMode) return;

    const nextStoredMode =
      normalizeStoredMode(orchestrationModeRef.current ?? orchestrationMode) === SESSION_MODE_PLANNING
        ? mapUiModeToStored(SESSION_MODE_AGENT)
        : SESSION_MODE_PLANNING;

    setOrchestrationModeImmediate(nextStoredMode);
    void persistSessionSettings({ OrchestrationMode: nextStoredMode });
  }, [forceDirectMode, orchestrationMode, persistSessionSettings, setOrchestrationModeImmediate]);

  // ---- helpers (per-session localStorage, not backward compatible) ----
  const LS_PREFIX = "ai:msgs:";
  const getMsgsKey = (id) => (id ? `${LS_PREFIX}${id}` : null);
  const getStoredMessages = (id) => {
    if (!id) return null;
    try {
      const raw = localStorage.getItem(getMsgsKey(id));
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  };
  const setStoredMessages = (id, messages) => {
    if (!id) return;
    try {
      localStorage.setItem(getMsgsKey(id), JSON.stringify(messages ?? []));
    } catch {}
  };

  const hasNonSystemMessages = (msgs) =>
    Array.isArray(msgs) &&
    msgs.some(
      (m) =>
        (m?.role || "").toLowerCase() !== "system" &&
        (m?.content || "").trim() !== ""
    );

  const isTabsArray = (val) =>
    Array.isArray(val) &&
    val.length > 0 &&
    val.every(
      (t) =>
        t &&
        typeof t === "object" &&
        typeof t.key === "string" &&
        (typeof t.title === "string" || typeof t.label === "string") &&
        "content" in t
    );

  const upsertById = (list, item) => {
    const arr = Array.isArray(list) ? list : [];
    const idx = arr.findIndex((x) => x && x._id === item._id);
    if (idx === -1) return [...arr, item];
    const clone = arr.slice();
    clone[idx] = { ...clone[idx], ...item };
    return clone;
  };

  const sortChatsBySequence = useCallback(
    (items) =>
      (Array.isArray(items) ? items : [])
        .slice()
        .sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0)),
    []
  );

  const updateChatMetaState = useCallback(
    (items) => {
      const ordered = sortChatsBySequence(items);
      setAllChats(ordered);
      setActiveChatMeta(ordered.length > 0 ? ordered[ordered.length - 1] : null);
      const latestChatItem = ordered[ordered.length - 1];
      const latestReviewDraft = isPendingReviewChatItem(latestChatItem)
        ? extractReviewDraftFromChatItem(latestChatItem)
        : null;
      if (latestReviewDraft) {
        setActiveReviewDraft(latestReviewDraft);
      }
      return ordered;
    },
    [sortChatsBySequence]
  );

  const first = (x) =>
    typeof x?.first === "function" ? x.first() : Array.isArray(x) ? x[0] : undefined;

  const looksLikeGuid = (value) => {
    const text = String(value ?? "").trim();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text);
  };

  const normalizeQueryText = (value) => {
    const text = String(value ?? "").trim();
    return text ? text.replace(/\s+/g, " ").trim().toLowerCase() : "";
  };

  const looksLikeQueryText = (value, generatedQuery = null) => {
    const text = String(value ?? "").trim();
    if (!text) return false;

    const normalized = normalizeQueryText(text);
    const normalizedGenerated = normalizeQueryText(generatedQuery);
    if (normalizedGenerated && normalized === normalizedGenerated) return true;

    return /^(select|with|insert|update|delete|merge|evaluate|let\b|from\b|\{)/i.test(text);
  };

  const looksLikeTechnicalStatus = (value) => {
    const text = String(value ?? "").trim();
    if (!text) return false;

    return /^(executing prompt|running |working through |sending your question|filtering your grid)/i.test(
      text
    );
  };

  const getAssistantContent = useCallback((item) => {
    const embeddedUiAction = extractEmbeddedUiAction(
      {
        Value: item?.Value ?? item?.value ?? null,
        ResponseMessage: item?.ResponseMessage ?? item?.responseMessage ?? null
      },
      item
    );
    if (embeddedUiAction?.comment) return embeddedUiAction.comment;

    const generatedQuery = item?.GeneratedQuery ?? item?.generatedQuery ?? null;
    const candidates = [
      item?.ResponseMessage,
      item?.ModelResponse
    ]
      .map((x) => String(x ?? "").trim())
      .filter(Boolean);

    const chatMessages = Array.isArray(item?.ChatMessages)
      ? item.ChatMessages
      : Array.isArray(item?.chatMessages)
      ? item.chatMessages
      : [];

    const orderedMessages = chatMessages
      .slice()
      .sort((left, right) => (left?.Sequence ?? 0) - (right?.Sequence ?? 0));

    const commentCandidates = orderedMessages
      .map((entry) => ({
        role: String(entry?.Role ?? entry?.role ?? "").trim().toLowerCase(),
        response: String(entry?.ResponseMessage ?? entry?.responseMessage ?? "").trim()
      }))
      .filter(
        (entry) =>
          entry.response &&
          !looksLikeGuid(entry.response) &&
          !looksLikeQueryText(entry.response, generatedQuery) &&
          !looksLikeTechnicalStatus(entry.response)
      );

    const legacyComment =
      commentCandidates
        .filter((entry) => entry.role === "assistant")
        .map((entry) => entry.response)
        .pop() ??
      commentCandidates
        .map((entry) => entry.response)
        .pop();

    const nonQueryCandidate = candidates.find(
      (value) => !looksLikeGuid(value) && !looksLikeQueryText(value, generatedQuery)
    );
    if (nonQueryCandidate) return nonQueryCandidate;

    if (legacyComment) return legacyComment;

    if (!candidates.length) {
      const legacyAssistant = orderedMessages
        .map((entry) =>
          String(
            entry?.ResponseMessage ??
              entry?.responseMessage ??
              entry?.Output ??
              entry?.output ??
              ""
          ).trim()
        )
        .filter(Boolean)
        .pop();

      if (!legacyAssistant) return "";
      return looksLikeGuid(legacyAssistant) ? "" : legacyAssistant;
    }

    const nonGuid = candidates.find((x) => !looksLikeGuid(x));
    return nonGuid ?? candidates[0];
  }, []);

  const getUserContent = useCallback((item) => {
    const direct = String(item?.Input ?? item?.input ?? "").trim();
    if (direct) return direct;

    const chatMessages = Array.isArray(item?.ChatMessages)
      ? item.ChatMessages
      : Array.isArray(item?.chatMessages)
      ? item.chatMessages
      : [];

    const legacyUser = chatMessages
      .slice()
      .sort((left, right) => (left?.Sequence ?? 0) - (right?.Sequence ?? 0))
      .map((entry) => String(entry?.InputMessage ?? entry?.inputMessage ?? "").trim())
      .find(Boolean);

    return legacyUser ?? "";
  }, []);

  const isPersistedErrorChatItem = useCallback((item) => {
    if (!item || typeof item !== "object") return false;

    return (
      hasTextValue(item?.DisplayErrorMessage ?? item?.displayErrorMessage) ||
      hasTextValue(item?.DebugErrorMessage ?? item?.debugErrorMessage) ||
      isFailedOutcomeStatus(item?.AIOutcomeStatus ?? item?.aiOutcomeStatus)
    );
  }, []);

  const resolvePersistedErrorBubbleContent = useCallback(
    (item) => {
      const assistantContent = getAssistantContent(item);
      if (assistantContent) return assistantContent;

      const displayError = String(
        item?.DisplayErrorMessage ?? item?.displayErrorMessage ?? ""
      ).trim();
      if (displayError) return displayError;

      const modelResponse = String(item?.ModelResponse ?? item?.modelResponse ?? "").trim();
      if (modelResponse) return modelResponse;

      const responseMessage = String(
        item?.ResponseMessage ?? item?.responseMessage ?? ""
      ).trim();
      if (responseMessage) return responseMessage;

      return "Request failed.";
    },
    [getAssistantContent]
  );

  const resolveEmptyAssistantResponseMessage = useCallback(
    (activeSession = null) => {
      const activeMode = processingModePresetSettings?.OrchestrationMode ??
        normalizeStoredMode(activeSession?.OrchestrationMode ?? orchestrationMode);

      if (isWarehouseQuestionOperation(operationName) && activeMode === SESSION_MODE_DIRECT) {
        return DIRECT_WAREHOUSE_AGENT_MODE_HINT_MESSAGE;
      }

      return GENERIC_EMPTY_RESPONSE_MESSAGE;
    },
    [operationName, orchestrationMode, processingModePresetSettings]
  );

  const toSessionIdText = (value) => {
    const raw = value?.ChatSessionId ?? value?.chatSessionId ?? value?.SessionId ?? value?.sessionId ?? null;
    return raw != null ? String(raw) : null;
  };

  const preloadedAiChatsBySession = useMemo(() => {
    const map = new Map();
    const items = Array.isArray(aiChats) ? aiChats : [];
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      if (item.IsActive === false || item.IsDeleted === true) continue;
      const sid = toSessionIdText(item);
      if (!sid) continue;
      const bucket = map.get(sid) || [];
      bucket.push(item);
      map.set(sid, bucket);
    }
    for (const [sid, bucket] of map.entries()) {
      bucket.sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0));
      map.set(sid, bucket);
    }
    return map;
  }, [aiChats]);

  const activeSessionSummary = useMemo(() => {
    if (activeChatMeta?._id) return activeChatMeta;
    const ordered = sortChatsBySequence(allChats);
    return ordered.length > 0 ? ordered[ordered.length - 1] : null;
  }, [activeChatMeta, allChats, sortChatsBySequence]);

  useEffect(() => {
    if (activeReviewDraft) return;

    const sessionReviewDraft = extractSessionReviewDraft(chatSession);
    if (sessionReviewDraft) return;

    const ordered = sortChatsBySequence(allChats);
    const latestChatItem = ordered[ordered.length - 1];
    const latestReviewDraft = isPendingReviewChatItem(latestChatItem)
      ? extractReviewDraftFromChatItem(latestChatItem)
      : null;
    if (latestReviewDraft) {
      setActiveReviewDraft(latestReviewDraft);
    }
  }, [activeReviewDraft, allChats, chatSession, sortChatsBySequence]);

  const recentSessionChats = useMemo(() => {
    const ordered = sortChatsBySequence(allChats);
    return ordered.slice(Math.max(ordered.length - 3, 0)).reverse();
  }, [allChats, sortChatsBySequence]);

  const normalizeOutputUiCommand = useCallback((value) => {
    return normalizePreviewUiCommandValue(value);
  }, []);

  const sessionReviewDrafts = useMemo(() => extractSessionReviewDrafts(chatSession), [chatSession]);

  const resolveChatItemForMessage = useCallback(
    (message) => {
      if (message?.val && typeof message.val === "object") return message.val;
      const messageId = message?._id != null ? String(message._id) : "";
      if (!messageId) return null;
      return (allChats || []).find((chat) => String(chat?._id ?? "") === messageId) || null;
    },
    [allChats]
  );

  const hasAssociatedOutputPayload = useCallback((chatItem) => {
    if (!chatItem || typeof chatItem !== "object") return false;
    const candidates = [
      chatItem.Output,
      chatItem.output,
      chatItem.ModelResponse,
      chatItem.modelResponse,
      chatItem.ResponseMessage,
      chatItem.responseMessage
    ];
    return candidates.some((value) => String(value ?? "").trim().length > 0);
  }, []);

  const resolvePreviewPanelUpdate = useCallback((chatResult, item0) => {
    const previewPayload = extractReviewPayload(REVIEW_TYPE_PREVIEW, chatResult, item0);
    if (!isRenderablePreviewPayload(previewPayload)) return null;

    const finalUiCommand = normalizeOutputUiCommand(
      getObjectPropCI(previewPayload, "finalUiCommand", "FinalUiCommand")
    );

    const panelValue =
      getObjectPropCI(previewPayload, "payload", "Payload", "output", "Output", "data", "Data") ??
      resolveChatResultValue(chatResult) ??
      null;

    if (panelValue == null) return null;

    return {
      kind: finalUiCommand,
      panelCommand: finalUiCommand,
      panelValue,
      iconKey: finalUiCommand === "data" ? "table" : "analytics",
      title: finalUiCommand === "data" ? "View associated table" : "View associated result"
    };
  }, [normalizeOutputUiCommand]);

  const resolveFilterAssociatedOutputTarget = useCallback((chatItem) => {
    if (!chatItem || typeof chatItem !== "object") return null;

    const embeddedUiAction = extractEmbeddedUiAction(
      {
        Value: chatItem?.Value ?? chatItem?.value ?? null,
        ResponseMessage: chatItem?.ResponseMessage ?? chatItem?.responseMessage ?? null,
        ModelResponse: chatItem?.ModelResponse ?? chatItem?.modelResponse ?? null,
        Output: chatItem?.Output ?? chatItem?.output ?? null
      },
      chatItem
    );

    const uiCommand = resolveUiCommandValue(chatItem) || embeddedUiAction?.uiAction;
    if (!isFilterUiCommand(uiCommand)) return null;

    const panelValue = resolveFilterPanelValue(chatItem, chatItem, embeddedUiAction?.args);
    if (!panelValue) return null;

    return {
      kind: FILTER_UI_COMMAND,
      panelCommand: FILTER_UI_COMMAND,
      panelValue,
      iconKey: "filterlist",
      title: "Apply filter"
    };
  }, []);

  const getAssociatedOutputTarget = useCallback(
    (message) => {
      const chatItem = resolveChatItemForMessage(message);
      const outputKind = resolveChatOutputKind(chatItem);
      const reviewState = resolveChatReviewState(chatItem, outputKind);
      if (outputKind === CHAT_OUTPUT_KIND_PREVIEW) {
        if (reviewState !== CHAT_REVIEW_STATE_PENDING) return null;
        const reviewDraft = extractReviewDraftFromChatItem(chatItem);
        if (!reviewDraft) return null;

        return {
          kind: REVIEW_TYPE_PREVIEW,
          iconKey: "opennewwindow",
          title: "Open preview",
          reviewDraft
        };
      }

      const filterTarget = resolveFilterAssociatedOutputTarget(chatItem);
      if (filterTarget) return filterTarget;

      const mode = resolveChatHistoryDisplayMode(chatItem);
      if (!["data", "render", "chart"].includes(mode)) return null;
      if (!hasAssociatedOutputPayload(chatItem)) return null;

      return {
        kind: mode,
        iconKey: mode === "data" ? "table" : "analytics",
        title: mode === "data" ? "View associated table" : "View associated result"
      };
    },
    [
      hasAssociatedOutputPayload,
      resolveFilterAssociatedOutputTarget,
      resolveChatItemForMessage
    ]
  );

  const handleOpenAssociatedOutput = useCallback(
    (message, target) => {
      const chatItem = resolveChatItemForMessage(message);
      if (!chatItem) return;

      if (String(target?.kind ?? "").trim().toLowerCase() === REVIEW_TYPE_PREVIEW) {
        const reviewDraft = target?.reviewDraft ?? extractReviewDraftFromChatItem(chatItem);
        if (!reviewDraft) return;
        setActiveChatMeta(chatItem);
        setActiveReviewDraft(reviewDraft);
        return;
      }

      if (String(target?.kind ?? "").trim().toLowerCase() === FILTER_UI_COMMAND) {
        if (typeof onUpdatePanel !== "function") return;
        const panelValue = target?.panelValue ?? resolveFilterPanelValue(chatItem, chatItem);
        if (!panelValue) return;
        setActiveChatMeta(chatItem);
        void onUpdatePanel(FILTER_UI_COMMAND, chatItem, panelValue);
        return;
      }

      if (typeof onOpenAssociatedOutput !== "function") return;
      onOpenAssociatedOutput(chatItem, target);
    },
    [onOpenAssociatedOutput, onUpdatePanel, resolveChatItemForMessage]
  );

  useEffect(() => {
    setIsLatestTurnCollapsed(true);
  }, [activeSessionSummary?._id, activeSessionSummary?.Sequence]);

  useEffect(() => {
    if (chatPane !== "right" || effectiveChatPaneCollapseMode === CHAT_PANE_COLLAPSE_DISABLED) {
      setIsChatPaneCollapsed(false);
      return;
    }

    setIsChatPaneCollapsed(effectiveChatPaneCollapseMode === CHAT_PANE_COLLAPSE_CLOSED);
  }, [chatPane, effectiveChatPaneCollapseMode]);

  const mapAiChatsToMessages = useCallback(
    (items) =>
      (items ?? [])
        .slice()
        .sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0))
        .flatMap((x) => [
          { role: "user", content: getUserContent(x), _id: x?._id },
          {
            role: isPersistedErrorChatItem(x)
              ? "error"
              : (x?.Role === "user" ? "assistant" : x?.Role) ?? "assistant",
            content: isPersistedErrorChatItem(x)
              ? resolvePersistedErrorBubbleContent(x)
              : getAssistantContent(x),
            val: x,
            _id: x?._id,
            isErrorTurn: isPersistedErrorChatItem(x)
          }
        ])
        .filter((m) => (m?.content ?? "").trim() !== ""),
    [getAssistantContent, getUserContent, isPersistedErrorChatItem, resolvePersistedErrorBubbleContent]
  );

  const formatError = (r) => {
    const normalizeText = (value) =>
      String(value ?? "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    const dedupeList = (values) => {
      const seen = new Set();
      const out = [];
      for (const raw of values) {
        const text = String(raw ?? "").trim();
        if (!text) continue;
        const key = normalizeText(text);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(text);
      }
      return out;
    };
    const collapseDuplicatedPair = (value) => {
      const text = String(value ?? "").trim();
      if (!text) return text;
      const tryCollapse = (parts) => {
        if (parts.length !== 2) return null;
        const left = String(parts[0] ?? "").trim();
        const right = String(parts[1] ?? "").trim();
        if (!left || !right) return null;
        return normalizeText(left) === normalizeText(right) ? left : null;
      };
      let collapsed = tryCollapse(text.split(/\s*;\s*/));
      if (collapsed) return collapsed;
      collapsed = tryCollapse(text.split(/\n{2,}/));
      if (collapsed) return collapsed;
      return text;
    };
    const extractEventProcessorMessage = (value) => {
      const text = String(value ?? "").trim();
      if (!/event processor error\s*:/i.test(text)) return null;

      const matches = [...text.matchAll(/"message"\s*:\s*"([^"]+)"/gi)];
      if (!matches.length) return null;

      const messages = dedupeList(matches.map((m) => (m?.[1] ?? "").trim()));
      return messages.length ? messages.join(" ") : null;
    };
    const getPropCI = (obj, ...names) => {
      if (!obj || typeof obj !== "object") return undefined;
      const keyMap = new Map(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
      for (const n of names) {
        const k = keyMap.get(String(n).toLowerCase());
        if (k !== undefined) return obj[k];
      }
      return undefined;
    };
    const asCsv = (arr) => {
      if (!Array.isArray(arr)) return undefined;
      const mapped = arr
        .map((x) => {
          if (x == null) return "";
          if (typeof x === "string") return x;
          if (typeof x === "object") {
            const inner = extractDetail(x);
            return inner != null && inner !== ""
              ? String(inner)
              : JSON.stringify(inner);
          }
          return String(x);
        })
        .filter(Boolean);
      return dedupeList(mapped).join(", ");
    };
    const extractDetail = (obj) => {
      if (obj == null) return undefined;
      if (typeof obj !== "object") return String(obj);
      const vDisplay = getPropCI(obj, "displaymessage");
      if (vDisplay != null)
        return typeof vDisplay === "object"
          ? extractDetail(vDisplay)
          : String(vDisplay);
      const vMessage = getPropCI(obj, "message");
      if (vMessage != null)
        return Array.isArray(vMessage)
          ? asCsv(vMessage) ?? String(vMessage)
          : typeof vMessage === "object"
          ? extractDetail(vMessage)
          : String(vMessage);
      const vError = getPropCI(obj, "error");
      if (vError != null)
        return typeof vError === "object"
          ? extractDetail(vError)
          : String(vError);
      const vErrMsg = getPropCI(obj, "errormessage");
      if (vErrMsg != null)
        return typeof vErrMsg === "object"
          ? extractDetail(vErrMsg)
          : String(vErrMsg);
      const vMsgDetails = getPropCI(obj, "messagedetails");
      if (vMsgDetails != null)
        return Array.isArray(vMsgDetails)
          ? asCsv(vMsgDetails)
          : typeof vMsgDetails === "object"
          ? extractDetail(vMsgDetails)
          : String(vMsgDetails);
      const vInfo = getPropCI(obj, "info");
      if (vInfo != null)
        return typeof vInfo === "object"
          ? extractDetail(vInfo)
          : String(vInfo);
      const vDetails = getPropCI(obj, "details");
      if (vDetails != null)
        return Array.isArray(vDetails)
          ? asCsv(vDetails)
          : typeof vDetails === "object"
          ? extractDetail(vDetails)
          : String(vDetails);
      return undefined;
    };
    let label = getPropCI(r, "DisplayMessage");
    if (label == null) {
      const messageValue = getPropCI(r, "Message");
      if (messageValue != null) label = extractDetail(messageValue);
    }
    if (label == null) {
      label = getPropCI(r, "AIOutcomeStatus", "Status") ?? "Error";
    }
    if (typeof label === "object")
      label = extractDetail(label) ?? "Error";
    label = String(label).trim();
    let detail = extractDetail(r);
    if (detail == null || detail === "") {
      const md = getPropCI(r, "MessageDetails");
      if (Array.isArray(md)) detail = asCsv(md);
    }
    if (detail == null) detail = getPropCI(r, "DisplayErrorMessage");
    if (detail == null) detail = "";
    const parsedEventMessage = extractEventProcessorMessage(detail);
    if (parsedEventMessage) {
      return label && label !== "Error" ? label : parsedEventMessage;
    }
    detail = collapseDuplicatedPair(detail);

    if (label) return label;
    return String(detail || "Error").trim();
  };

  const resolveDebugErrorDetail = (result) => {
    const toDebugText = (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value.trim();
      if (Array.isArray(value)) {
        return value
          .map((entry) => toDebugText(entry))
          .filter(Boolean)
          .join("\n")
          .trim();
      }
      if (typeof value === "object") {
        try {
          return JSON.stringify(value, null, 2).trim();
        } catch {
          return String(value).trim();
        }
      }
      return String(value).trim();
    };

    const resultItem = first(result?.Items) ?? result?.Item ?? null;
    const candidates = [
      result?.DebugErrorMessage,
      result?.debugErrorMessage,
      result?.MessageDetails,
      result?.Message?.Info,
      result?.Exception,
      resultItem?.DebugErrorMessage,
      resultItem?.debugErrorMessage,
      resultItem?.MessageDetails,
      resultItem?.Message?.Info,
      resultItem?.Exception
    ];

    for (const candidate of candidates) {
      const text = toDebugText(candidate);
      if (text) return text;
    }

    return "";
  };

  const resolveUiActionMessage = (chatResult, item0) => {
    const previewPayload = extractReviewPayload(REVIEW_TYPE_PREVIEW, chatResult, item0);
    if (!isRenderablePreviewPayload(previewPayload)) {
      const previewMessage = resolvePreviewPayloadMessage(previewPayload);
      if (previewMessage) {
        return previewMessage;
      }
    }

    const embeddedUiAction = extractEmbeddedUiAction(chatResult, item0);
    const value = resolveChatResultValue(chatResult);
    const valueComment =
      value?.comment ??
      value?.Comment ??
      value?.message ??
      value?.Message ??
      value?.answer ??
      value?.Answer;

    return (
      embeddedUiAction?.comment ||
      getAssistantContent(item0) ||
      String(chatResult?.ModelResponse ?? "").trim() ||
      String(chatResult?.ResponseMessage ?? "").trim() ||
      String(valueComment ?? "").trim() ||
      "Updating..."
    );
  };

  // ---- Splash text helpers ----
  const getSplashText = () => {
    if (typeof splashMessage === "string" && splashMessage.trim().length > 0) {
      return splashMessage.trim();
    }
    if (referenceName) {
      return `Ask me a question about your ${referenceName}.`;
    }
    return "Ask me a question about your data.";
  };

  const makeSystemMessage = () => ({
    role: "system",
    content: getSplashText()
  });

  // ---------- Load an existing session by id & hydrate (dedup + lock) ----------
  const loadSessionById = useCallback(
    async (id, sessionObj = null) => {
      if (!id) return;

      // Pin locally; do not allow parent !session to reset UI while we're in control
      localPinnedRef.current = true;
      setUiMode("chat");

      // If this id already loaded successfully, bail.
      if (lastLoadedIdRef.current === id) {
        setSelectedSessionId(id);
        if (!chatSession)
          setChatSession({
            _id: id,
            ReferenceId: referenceId,
            ReferenceItemName: referenceName
          });
        return;
      }

      // In-flight protection
      if (loadingSessionsRef.current.has(id)) return;
      loadingSessionsRef.current.add(id);

      try {
        // reflect selection & create placeholder session (prevents snapping)
        setSelectedSessionId(id);

        if (!chatSession)
          setChatSession({
            _id: id,
            ReferenceId: referenceId,
            ReferenceItemName: referenceName
          });

        // per-session storage now; nothing to clear globally
        setSessionChatMessages(undefined); // loading
        setIsLoadingHistory(true); // gate UI switch

        const sid = String(id);
        const preloadedForSession = preloadedAiChatsBySession.get(sid) || [];
        // Use injected AIChat items first to avoid extra network calls.
        const r =
          preloadedForSession.length > 0
            ? { Success: true, Items: preloadedForSession }
            : await aiChatMgr.Query({
                model:
                  `x => x.IsActive == true && x.IsDeleted == false && x.ChatSessionId == sessionId`,
                params: { sessionId: id }
              });

        if (r?.Success) {
          updateChatMetaState(r?.Items ?? []);
          const chats = mapAiChatsToMessages(r?.Items ?? []);

          // hydrate session object
          let resolvedSession = null;
          if (sessionObj) {
            setChatSession(sessionObj);
            resolvedSession = sessionObj;
            applySessionSettings(sessionObj);
          } else {
            const resp = await aiSessionMgr.getItem(id);
            if (resp?.Success) {
              resolvedSession =
                resp.Items.first?.() ??
                first(resp.Items) ??
                resp.Items ?? {
                  _id: id,
                  ReferenceId: referenceId,
                  ReferenceItemName: referenceName
                };
              applySessionSettings(resolvedSession);
              setChatSession(resolvedSession);
            } else {
              resolvedSession = {
                _id: id,
                ReferenceId: referenceId,
                ReferenceItemName: referenceName
              };
              setChatSession((prev) => prev ?? resolvedSession);
            }
          }

          setSessionChatMessages(chats);
          if (typeof onSessionChange === "function") {
            onSessionChange(id, resolvedSession, r?.Items ?? []);
          }
          lastLoadedIdRef.current = id; // mark success
      } else {
        updateChatMetaState([]);
        setSessionChatMessages([]); // keep chat mode
        setChatSession((prev) =>
          prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
        );
      }
    } catch {
      updateChatMetaState([]);
      setSessionChatMessages([]); // keep chat mode
      setChatSession((prev) =>
        prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
        );
      } finally {
        setIsLoadingHistory(false);
        loadingSessionsRef.current.delete(id); // release lock
      }
    },
    [aiChatMgr, aiSessionMgr, chatSession, referenceId, referenceName, preloadedAiChatsBySession, mapAiChatsToMessages, onSessionChange]
  );

  // When the `session` prop changes:
  useEffect(() => {
    if (session && session._id) {
      const sessionId = String(session._id);
      if (ignoredSessionPropIdRef.current && ignoredSessionPropIdRef.current !== sessionId) {
        ignoredSessionPropIdRef.current = null;
      }
      if (ignoredSessionPropIdRef.current === sessionId) {
        return;
      }

      setUiMode("chat");
      setChatSession(session);
      applySessionSettings(session);
      setSelectedSessionId(session._id);
      if (typeof onSessionChange === "function") {
        onSessionChange(session._id, session);
      }
      localPinnedRef.current = true; // respect parent-provided session as "pinned"
      if (lastLoadedIdRef.current !== session._id) {
        loadSessionById(session._id, session);
      }
      return;
    }

    if (session && !session._id) {
      setUiMode("chat");
      setChatSession(session);
      applySessionSettings(session);
      localPinnedRef.current = true;
      return;
    }

    // session is falsy
    if (localPinnedRef.current) {
      // Ignore transient parent clears while we control the selection
      return;
    }

    if (
      sessionChatMessages === null &&
      chatSession == null &&
      selectedSessionId == null &&
      allChats.length === 0 &&
      activeChatMeta == null &&
      uiMode === "startup"
    ) {
      return;
    }

    // Explicit clear from parent while not locally pinned resets to startup
    setSessionChatMessages(null);
    setChatSession(null);
    setSelectedSessionId(null);
    setAllChats([]);
    setActiveChatMeta(null);
    setActiveReviewDraft(null);
    lastLoadedIdRef.current = null;
    loadingSessionsRef.current.clear();
    setUiMode("startup");
  }, [applySessionSettings, onSessionChange, session]);

  // ---------- AI Chat plumbing ----------
  const handleInit = (api) => {
    setChatApi((prev) => (prev === api ? prev : api));

    if (initSeededRef.current) {
      setChatUiReady(true);
      return;
    }

    try {
      const knownId = session?._id || selectedSessionId;
      const saved = getStoredMessages(knownId);
      const hasSaved = Array.isArray(saved) && saved.length > 0;
      const hasSessionMsgs =
        Array.isArray(sessionChatMessages) && sessionChatMessages.length > 0;
      const hasStart =
        typeof startChat === "string" && startChat.trim().length > 0;

      // For persisted sessions, prefer API-loaded messages over local cache.
      if (hasSessionMsgs) {
        api.setMessages(sessionChatMessages);
      } else if (!knownId && hasSaved) {
        // Only use local cache for unsaved/ephemeral sessions.
        api.setMessages(saved);
      } else if (!hasStart) {
        // seed with system-only splash, which will now match visible splash text
        api.setMessages([makeSystemMessage()]);
      }
    } catch {}

    initSeededRef.current = true;
    setChatUiReady(true);
  };

  // Keep UI in sync when history arrives after init
  useEffect(() => {
    if (!chatUiReady) return;
    if (!chatApi) return;
    if (Array.isArray(sessionChatMessages)) {
      const msgs =
        sessionChatMessages.length > 0
          ? sessionChatMessages
          : [makeSystemMessage()];
      chatApi.setMessages(msgs);
      const id = session?._id || selectedSessionId;
      setStoredMessages(id, msgs);
    }
  }, [chatUiReady, chatApi, sessionChatMessages]);

  const handleSave = (messages) => {
    const id = chatSession?._id || selectedSessionId;
    setStoredMessages(id, messages);
  };

  const handleSend = async (userText) => {
    if (inFlightSendRef.current)
      return { role: "assistant", content: "" };
    inFlightSendRef.current = true;
    const runOrchestrationMode = processingModePresetSettings?.OrchestrationMode ??
      normalizeStoredMode(orchestrationModeRef.current ?? orchestrationMode);
    try {
      setThinking(STARTING_REQUEST_MESSAGE);
      const preparedContext = typeof onBeforeSend === "function"
        ? await onBeforeSend({
            userText,
            referenceId,
            referenceName,
            params,
            payload,
            session: chatSession
          })
        : null;
      if (preparedContext?.Success === false) {
        throw new Error(
          preparedContext?.DisplayMessage ||
          preparedContext?.displayMessage ||
          "The AI request context could not be prepared."
        );
      }
      const effectiveReferenceId = preparedContext?.referenceId ?? referenceId;
      const effectiveReferenceName = preparedContext?.referenceName ?? referenceName;
      const effectiveParams = preparedContext?.params ?? params;
      const effectivePayload = preparedContext?.payload ?? payload;
      let currentSession = {
        ...(preparedContext?.session ?? chatSession ?? {}),
        ReferenceId: effectiveReferenceId,
        ReferenceItemName: effectiveReferenceName
      };

      if (currentSession?._id === undefined) {
        setThinking(CREATING_SESSION_MESSAGE);
        const sessionResult = await aiSessionMgr.saveItem({
          ...currentSession,
          ...buildSessionSettings(),
          OrchestrationMode: runOrchestrationMode,
          Name: "New Session"
        });
        currentSession =
          sessionResult?.Items?.first?.() ??
          first(sessionResult?.Items) ??
          sessionResult?.Items ??
          null;
        setChatSession(currentSession);
        setSelectedSessionId(currentSession?._id ?? null);
        if (currentSession?._id) {
          lastLoadedIdRef.current = String(currentSession._id);
          if (typeof onSessionChange === "function") {
            onSessionChange(currentSession._id, currentSession, allChats);
          }
        }
        void loadSessionSelectData();
        localPinnedRef.current = true;
        setUiMode("chat");
      }

      if (processingModePresetSettings && currentSession?._id) {
        const needsPresetPatch =
          normalizeStoredMode(currentSession?.OrchestrationMode) !== processingModePresetSettings.OrchestrationMode ||
          String(currentSession?.ApprovalPolicyOverride ?? "") !== processingModePresetSettings.ApprovalPolicyOverride ||
          normalizeStoredProfileKey(currentSession?.AILevelProfileKey) !== processingModePresetSettings.AILevelProfileKey;

        if (needsPresetPatch) {
          const patchedSession = {
            ...currentSession,
            ...processingModePresetSettings
          };
          const saveResult = await aiSessionMgr.saveItem(patchedSession);
          currentSession =
            saveResult?.Items?.first?.() ??
            first(saveResult?.Items) ??
            saveResult?.Items ??
            patchedSession;
          setChatSession(currentSession);
          applySessionSettings(currentSession);
          if (typeof onSessionChange === "function") {
            onSessionChange(currentSession?._id, currentSession, allChats);
          }
        }
      }

      const hubController = await aiSessionMgr.GetStream({
        operationName,
        id: currentSession?._id,
        type: "sse"
      });
      if (hubController?.Success && typeof hubController.onUI === "function") {
        hubController.onUI("thinking", (msg) => {
          const incoming = (msg?.Message ?? "").trim();
          if (isPlaceholderThinkingMessage(incoming)) {
            setThinking(GENERIC_THINKING_MESSAGE);
          } else {
            setThinking(incoming);
          }
        });
      } else {
        setThinking(GENERIC_THINKING_MESSAGE);
      }

      const pendingReviewAction = pendingReviewActionRef.current;
      const activeReviewDraftBeforeSend = activeReviewDraft;
      pendingReviewActionRef.current = null;
      if (!pendingReviewAction) {
        setActiveReviewDraft(null);
      }

      const chatResult = await aiSessionMgr.RunOperation({
        operationName,
        item: {
          sessionid: currentSession?._id,
          referenceid: effectiveReferenceId,
          message: userText,
          orchestrationMode: runOrchestrationMode,
          OrchestrationMode: runOrchestrationMode,
          ...(effectiveParams ?? {}),
          ...(pendingReviewAction ?? {}),
          payload: effectivePayload
        },
        isItem: false
      });

      if (chatResult?.Success === false) {
        await refreshSessionState(currentSession?._id);
        const errorMessage = formatError(chatResult);
        const debugErrorMessage = resolveDebugErrorDetail(chatResult);
        return {
          role: "error",
          content: errorMessage,
          displayErrorMessage: errorMessage,
          debugErrorMessage: debugErrorMessage || undefined,
          isErrorTurn: true
        };
      }

      const returnedItem0 = first(chatResult?.Items) ?? {};
      let item0 = returnedItem0;

      try {
        const persistedChatsResult = await aiChatMgr.Query({
          model:
            `x => x.IsActive == true && x.IsDeleted == false && x.ChatSessionId == sessionId`,
          params: { sessionId: currentSession?._id }
        });
        if (persistedChatsResult?.Success) {
          const persistedChats = sortChatsBySequence(persistedChatsResult?.Items ?? []);
          const returnedItemId = String(returnedItem0?._id ?? "").trim();
          const knownChatIds = new Set(
            (Array.isArray(allChats) ? allChats : [])
              .map((chat) => String(chat?._id ?? "").trim())
              .filter(Boolean)
          );
          const persistedResultItem =
            (returnedItemId
              ? persistedChats.find((chat) => String(chat?._id ?? "") === returnedItemId)
              : null) ??
            persistedChats
              .slice()
              .reverse()
              .find((chat) => {
                const chatId = String(chat?._id ?? "").trim();
                return chatId && !knownChatIds.has(chatId);
              }) ??
            null;

          if (persistedResultItem) {
            item0 =
              returnedItemId && String(persistedResultItem?._id ?? "") === returnedItemId
                ? { ...returnedItem0, ...persistedResultItem }
                : persistedResultItem;
          }
          updateChatMetaState(persistedChats);
        }
      } catch {}

      const panelItem = item0?._id ? item0 : chatResult?._id ? chatResult : null;
      const embeddedUiAction = extractEmbeddedUiAction(chatResult, item0);
      const directUiAction = resolveDirectUiAction(chatResult);
      const directUiActionPayload = resolveUiActionArgsPayload(
        getObjectPropCI(chatResult, "args", "Args")
      );
      const embeddedUiActionPayload = resolveUiActionArgsPayload(embeddedUiAction?.args);
      const resultItem =
        (directUiAction && chatResult?._id ? chatResult : null) ??
        (item0?._id ? item0 : null) ??
        (panelItem?._id ? panelItem : null);
      const itemUiAction = String(
        resultItem?.UICommand ??
        resultItem?.uiCommand ??
        resultItem?.UiCommand ??
        ""
      ).trim();

      const handleUiActionResult = async (uiAction, valueFallback = null) => {
        const normalizedUiAction = String(uiAction ?? "").trim().toLowerCase();
        let nextReviewDraft = null;
        let shouldClearReviewDraft = false;

        try {
          if (normalizedUiAction === REVIEW_TYPE_PLAN) {
            const extractedPlanDraft = createReviewDraft(
              REVIEW_TYPE_PLAN,
              extractReviewPayload(REVIEW_TYPE_PLAN, chatResult, panelItem ?? item0)
            );
            if (hasMeaningfulReviewDraft(extractedPlanDraft)) {
              nextReviewDraft = extractedPlanDraft;
            } else if (
              activeReviewDraftBeforeSend?.kind === REVIEW_TYPE_PLAN &&
              hasMeaningfulReviewDraft(activeReviewDraftBeforeSend)
            ) {
              nextReviewDraft = activeReviewDraftBeforeSend;
            }
          } else if (normalizedUiAction === REVIEW_TYPE_PREVIEW) {
            const previewTarget = resolvePreviewPanelUpdate(chatResult, panelItem ?? item0);
            if (previewTarget) {
              nextReviewDraft = createReviewDraft(
                REVIEW_TYPE_PREVIEW,
                extractReviewPayload(REVIEW_TYPE_PREVIEW, chatResult, panelItem ?? item0)
              );
            } else {
              shouldClearReviewDraft = true;
            }
          } else if (onUpdatePanel) {
            shouldClearReviewDraft = true;
            const previewDraftFallback =
              normalizedUiAction === "update" &&
              activeReviewDraftBeforeSend?.kind === REVIEW_TYPE_PREVIEW
                ? activeReviewDraftBeforeSend.payload
                : null;
            const updateValueCandidates = [
              valueFallback,
              resolveChatResultValue(chatResult),
              resolveChatResultValue(resultItem ?? panelItem ?? item0),
              chatResult?.Value,
              chatResult?.value,
              chatResult?.Output,
              chatResult?.output,
              previewDraftFallback,
              resultItem ?? panelItem ?? item0
            ];
            const updatePanelValue = updateValueCandidates
              .map((candidate) => parseMaybeJsonValue(candidate))
              .find((candidate) => candidate && typeof candidate === "object") ??
              valueFallback ??
              resolveChatResultValue(chatResult) ??
              previewDraftFallback;
            const panelValue = normalizedUiAction === FILTER_UI_COMMAND
              ? (
                  valueFallback ??
                  resolveFilterPanelValue(chatResult, panelItem ?? item0, resultItem ?? panelItem ?? item0) ??
                  resolveChatResultValue(chatResult)
                )
              : updatePanelValue;
            await onUpdatePanel(uiAction, resultItem ?? panelItem, panelValue);
          }
        } catch {}

        if (resultItem?._id) {
          const nextChats = sortChatsBySequence(upsertById(allChats, resultItem));
          setAllChats(nextChats);
          setActiveChatMeta(resultItem);
          await refreshSessionState(currentSession?._id, nextChats);
        } else {
          await refreshSessionState(currentSession?._id);
        }
        if (nextReviewDraft) {
          setActiveReviewDraft(nextReviewDraft);
        } else if (shouldClearReviewDraft) {
          setActiveReviewDraft(null);
        }

        return {
          content: resolveUiActionMessage(chatResult, panelItem ?? item0),
          _id: resultItem?._id ?? panelItem?._id,
          role: (resultItem ?? panelItem ?? item0)?.Role || "assistant"
        };
      };

      // UICommands (operation pathway)
      if (chatResult?.UICommands && chatResult.UICommands["AIAction"]) {
        return handleUiActionResult(
          String(chatResult.UICommands["AIAction"] ?? "").trim(),
          resolveChatResultValue(chatResult) ??
            resolveChatResultValue(resultItem ?? panelItem ?? item0) ??
            directUiActionPayload
        );
      }

      if (directUiAction) {
        return handleUiActionResult(directUiAction, directUiActionPayload);
      }

      if (itemUiAction && itemUiAction.toLowerCase() !== "none") {
        const matchingEmbeddedPayload =
          embeddedUiAction?.uiAction &&
          embeddedUiAction.uiAction.toLowerCase() === itemUiAction.toLowerCase()
            ? embeddedUiActionPayload
            : null;
        return handleUiActionResult(
          itemUiAction,
          matchingEmbeddedPayload ??
            resolveChatResultValue(resultItem ?? panelItem ?? item0)
        );
      }

      if (embeddedUiAction?.uiAction) {
        return handleUiActionResult(embeddedUiAction.uiAction, embeddedUiActionPayload);
      }

      // Normal assistant pathway
      if (resultItem?._id) {
        const nextChats = sortChatsBySequence(upsertById(allChats, resultItem));
        setAllChats(nextChats);
        setActiveChatMeta(resultItem);
        await refreshSessionState(currentSession?._id, nextChats);
      } else {
        await refreshSessionState(currentSession?._id);
      }
      const content =
        getAssistantContent(resultItem ?? panelItem ?? item0) ||
        String(chatResult?.ModelResponse ?? "").trim() ||
        String(chatResult?.ResponseMessage ?? "").trim() ||
        resolveEmptyAssistantResponseMessage(currentSession);
      const role =
        isPersistedErrorChatItem(resultItem ?? panelItem ?? item0)
          ? "error"
          : (resultItem ?? panelItem ?? item0)?.Role &&
            String((resultItem ?? panelItem ?? item0).Role).toLowerCase() !== "user"
          ? (resultItem ?? panelItem ?? item0).Role
          : "assistant";

      return {
        content,
        _id: resultItem?._id ?? panelItem?._id,
        role,
        isErrorTurn: isPersistedErrorChatItem(resultItem ?? panelItem ?? item0)
      };
    } finally {
      inFlightSendRef.current = false;
      const deferredSettings = deferredSessionSettingsRef.current;
      if (deferredSettings) {
        deferredSessionSettingsRef.current = null;
        void persistSessionSettings(deferredSettings);
      }
    }
  };

  const handleOpenDetails = useCallback(async (aiChatId, options = {}) => {
    if (!aiChatId) return;
    setSelectedChatDefaultTab(options?.defaultTab === "info" ? "info" : "flow");
    setSelectedChat(null);
    setLocalErrorDetail(null);
    setShowChatDialog(true);

    const cached = (allChats || []).find((c) => c && c._id === aiChatId);
    if (cached) {
      setSelectedChat(cached);
      setShowChatDialog(true);
      return;
    }

    const resp = await aiChatMgr.getItem(aiChatId);
    if (!resp?.Success || !resp.Items) return;

    const items = Array.isArray(resp.Items) ? resp.Items : [resp.Items];
    const item = items[0];
    if (!item?._id) return;

    setAllChats((prev) => upsertById(prev, item));
    setSelectedChat(item);
  }, [aiChatMgr, allChats]);

  const handleOpenMessageDetails = useCallback(
    async (message) => {
      if (!message) return;

      if (message?._id) {
        await handleOpenDetails(message._id, {
          defaultTab: message?.isErrorTurn ? "info" : "flow"
        });
        return;
      }

      const errorMessage = String(
        message?.content ??
          message?.DisplayErrorMessage ??
          message?.displayErrorMessage ??
          ""
      ).trim();
      const debugErrorMessage = String(
        message?.DebugErrorMessage ??
          message?.debugErrorMessage ??
          ""
      ).trim();

      setSelectedChat(null);
      setSelectedChatDefaultTab("info");
      setLocalErrorDetail({
        message: errorMessage || "Error while connecting to server, please try again.",
        debugErrorMessage
      });
      setShowChatDialog(true);
    },
    [handleOpenDetails]
  );

  const handleApprovalAction = useCallback(
    async (decision) => {
      if (!chatApi?.submitMessage || !isApprovalPending(activeSessionSummary?.ApprovalStatus)) return;

      const normalizedDecision = String(decision ?? "").trim().toLowerCase();
      if (normalizedDecision !== "approve" && normalizedDecision !== "deny") return;

      setApprovalActionInFlight(normalizedDecision);
      try {
        await chatApi.submitMessage(normalizedDecision);
      } finally {
        setApprovalActionInFlight(null);
      }
    },
    [activeSessionSummary?.ApprovalStatus, chatApi]
  );

  const isPreviewRefreshAvailable = useCallback(
    (reviewDraft) => resolvePreviewRefreshAvailability({
      reviewDraft,
      operationName,
      previewRefreshEnabled
    }),
    [operationName, previewRefreshEnabled]
  );

  const handleReviewAction = useCallback(
    async (action, label) => {
      const normalizedAction = String(action ?? "").trim();
      if (!normalizedAction) return;

      if (normalizedAction === "revise") {
        setToasterMessage("Type the changes you want and send them to revise this review.");
        chatApi?.focusInput?.();
        return;
      }

      if (!chatApi?.submitMessage) return;

      const draftId = activeReviewDraft?.draftId ?? null;
      const isAcceptPreview = normalizedAction.toLowerCase() === "acceptpreview";
      const planningDraftJson =
        activeReviewDraft?.kind === REVIEW_TYPE_PLAN
          ? stringifyReviewPayload(activeReviewDraft.payload)
          : "";
      pendingReviewActionRef.current = {
        reviewAction: normalizedAction,
        planningAction: normalizedAction,
        reviewDraftId: draftId,
        planningDraftId: draftId,
        ...(planningDraftJson ? { reviewDraftJson: planningDraftJson, planningDraftJson } : {}),
        ...(isAcceptPreview && isPreviewRefreshAvailable(activeReviewDraft) ? { enableRefresh: enablePreviewRefresh === true } : {})
      };

      setReviewActionInFlight(normalizedAction);
      try {
        await chatApi.submitMessage(label || normalizedAction);
        if (
          isAcceptPreview &&
          activeReviewDraft?.kind === REVIEW_TYPE_PREVIEW &&
          normalizePreviewUiCommandValue(activeReviewDraft.finalUiCommand) === "update" &&
          typeof onUpdatePanel === "function"
        ) {
          await onUpdatePanel("update", null, activeReviewDraft.payload);
        }
        if (normalizedAction.toLowerCase().startsWith("cancel") || isFinalReviewAction(normalizedAction)) {
          setActiveReviewDraft(null);
        }
      } finally {
        setReviewActionInFlight(null);
      }
    },
    [activeReviewDraft, chatApi, enablePreviewRefresh, isPreviewRefreshAvailable, onUpdatePanel]
  );

  const availableReviewDrafts = useMemo(() => {
    const chatDrafts = collectReviewDraftsFromChats(allChats);

    return {
      plan:
        activeReviewDraft?.kind === REVIEW_TYPE_PLAN
          ? activeReviewDraft
          : sessionReviewDrafts.plan ?? chatDrafts.plan ?? null,
      preview:
        activeReviewDraft?.kind === REVIEW_TYPE_PREVIEW
          ? activeReviewDraft
          : sessionReviewDrafts.preview ?? chatDrafts.preview ?? null
    };
  }, [activeReviewDraft, allChats, sessionReviewDrafts]);

  const handleStartChat = useCallback(() => {
    if (!chatSession) {
      // per-session storage now; nothing to clear globally
      const seed = {
        ReferenceId: referenceId,
        ReferenceItemName: referenceName,
        ...buildSessionSettings()
      };
      setChatSession(seed);
      setSelectedSessionId(null);
    }

    localPinnedRef.current = true;
    setUiMode("chat");
  }, [buildSessionSettings, chatSession, referenceId, referenceName]);

  useEffect(() => {
    if (!forceSingleSessionMode) return;
    if (uiMode === "chat") return;
    if (isLoadingHistory || sessionChatMessages === undefined) return;
    handleStartChat();
  }, [forceSingleSessionMode, handleStartChat, isLoadingHistory, sessionChatMessages, uiMode]);

  // ---- Auto-start: ONLY when there's no provided session id, and not while history is loading
  useEffect(() => {
    if (!(typeof startChat === "string" && startChat.trim().length > 0)) return;
    if (session?._id) return;
    if (sessionChatMessages === undefined) return;

    const key = `${referenceId ?? "none"}|${operationName ?? "op"}|${startChat.trim()}`;
    if (hasAutoStartedRef.current === key) return;
    if (didAutoStart) return;

    const savedMsgs = getStoredMessages(session?._id || selectedSessionId);
    const anyExisting =
      hasNonSystemMessages(savedMsgs) ||
      (Array.isArray(sessionChatMessages) &&
        sessionChatMessages.length > 0);

    if (anyExisting) return;

    if (!chatSession) {
      setChatSession({
        ReferenceId: referenceId,
        ReferenceItemName: referenceName
      });
      localPinnedRef.current = true;
      setUiMode("chat");
    }

    setPendingAutoPrompt(startChat.trim());
    hasAutoStartedRef.current = key;
  }, [
    startChat,
    chatSession,
    didAutoStart,
    sessionChatMessages,
    referenceName,
    referenceId,
    operationName,
    session,
    selectedSessionId
  ]);

  // Once chat UI is ready, send the pending prompt WITHOUT seeding [system,user] beforehand
  useEffect(() => {
    const run = async () => {
      if (!pendingAutoPrompt) return;
      if (!chatSession) return;
      if (!chatUiReady) return;
      if (autoStartInProgressRef.current) return;
      if (didAutoStart) return;

      const savedMsgs = getStoredMessages(session?._id || selectedSessionId);
      const anyExisting =
        hasNonSystemMessages(savedMsgs) ||
        (Array.isArray(sessionChatMessages) &&
          sessionChatMessages.length > 0);
      if (anyExisting) {
        setDidAutoStart(true);
        setPendingAutoPrompt(null);
        return;
      }

      autoStartInProgressRef.current = true;
      setDidAutoStart(true);

      const userText = pendingAutoPrompt;

      try {
        const systemMsg = makeSystemMessage();
        const assistantMsg = await handleSend(userText);
        const assistant = Array.isArray(assistantMsg)
          ? assistantMsg[0]
          : assistantMsg;

        const msgs = [
          systemMsg,
          { role: "user", content: userText },
          assistant ?? { role: "assistant", content: "" }
        ];
        chatApi?.setMessages?.(msgs);
        const id = chatSession?._id || selectedSessionId;
        setStoredMessages(id, msgs);
        setPendingAutoPrompt(null);
        localPinnedRef.current = true;
        setUiMode("chat");
      } catch {
        setPendingAutoPrompt(null);
      }
    };
    run();
  }, [
    pendingAutoPrompt,
    chatSession,
    chatUiReady,
    didAutoStart,
    sessionChatMessages,
    session,
    selectedSessionId
  ]);

  // Populate session selector for the current reference.
  useEffect(() => {
    void loadSessionSelectData();
  }, [loadSessionSelectData]);

  // --- Robust id coercion for FieldControl value shapes ---
  const coerceSessionId = (val) => {
    if (val == null) return null;
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (Array.isArray(val)) return val.length ? coerceSessionId(val[0]) : null;
    if (typeof val === "object") {
      return (
        val.Value ??
        val.value ??
        val._id ??
        val.Id ??
        val.id ??
        val.Key ??
        null
      );
    }
    return null;
  };

  const handleSelectSession = (valueOrItem, maybeItem) => {
    const id = coerceSessionId(valueOrItem) ?? coerceSessionId(maybeItem);
    if (!id) return;

    initSeededRef.current = false;
    setChatApi(null);
    setChatUiReady(false);
    setChatInstanceKey((value) => value + 1);

    // Pin UI to chat immediately and reflect selection
    localPinnedRef.current = true;
    setUiMode("chat");
    setSelectedSessionId(id);
    setChatSession((prev) =>
      prev ?? { _id: id, ReferenceId: referenceId, ReferenceItemName: referenceName }
    );

    // Per-session storage; nothing to clear globally
    setSessionChatMessages(undefined);
    setIsLoadingHistory(true);
    loadSessionById(id);
  };

  const handleBackToSessionPicker = () => {
    const currentSessionId = chatSession?._id ?? selectedSessionId ?? session?._id;
    if (currentSessionId) {
      ignoredSessionPropIdRef.current = String(currentSessionId);
    }

    // Return to startup session selector and clear active session hydration state.
    localPinnedRef.current = false;
    setUiMode("startup");
    setSelectedSessionId(null);
    setChatSession(null);
    setAllChats([]);
    setActiveChatMeta(null);
    setActiveReviewDraft(null);
    setSessionChatMessages(null);
    setIsLoadingHistory(false);
    setChatApi(null);
    setChatUiReady(false);
    setThinking("");
    initSeededRef.current = false;
    lastLoadedIdRef.current = null;
    loadingSessionsRef.current.clear();
    void loadSessionSelectData();
    if (typeof onSessionChange === "function") {
      onSessionChange(null, null);
    }
  };

  const handleSplitPaneResize = useCallback((sizes) => {
    setSplitPaneSize(sizes ?? { left: 0, middle: 0, right: 0, total: 0 });
  }, []);

  const renderSessionControlBar = useCallback(
    (className = "") => {
      if (hasFixedProcessingMode) return null;

      const controlMode = mapFriendlyModeToControlMode(friendlySessionState.mode);
      const isPlanningActive = friendlySessionState.mode === SESSION_MODE_PLANNING;
      const canShowPlanningButton =
        !forceDirectMode &&
        Boolean(operationName) &&
        (controlMode === SESSION_MODE_AGENT || isPlanningActive);

      return (
        <div className={["ai-session-control-bar", className].filter(Boolean).join(" ")}>
          {canShowPlanningButton ? (
              <button
                type="button"
                className={[
                  "ai-session-control-button",
                  isPlanningActive ? "ai-session-control-button--active" : ""
                ].filter(Boolean).join(" ")}
                aria-pressed={isPlanningActive}
                onClick={handlePlanningSelection}
              >
                <span>Plan</span>
                <span className="ai-session-control-button__icon ai-session-control-button__icon--plan" aria-hidden="true">
                  <IconControl iconKey="star_shine" />
                </span>
              </button>
            ) : null}
          {CONTROL_GROUPS.map((group) => {
            if (forceDirectMode && (group.key === "mode" || group.key === "speed")) {
              return null;
            }

            const selectedValue =
              group.key === "mode"
                ? controlMode
                : friendlySessionState[group.key];
            const isModeLocked = forceDirectMode && group.key === "mode";
            const isSpeedLocked = group.key === "speed" && controlMode === SESSION_MODE_DIRECT;
            const isControlDisabled = isModeLocked || isSpeedLocked;
            const options = isModeLocked ? [SESSION_MODE_DIRECT] : group.options;
            const onSelect =
              group.key === "mode"
                ? handleModeSelection
                : group.key === "approval"
                ? handleApprovalSelection
                : handleSpeedSelection;

            return (
              <div
                key={group.key}
                className="ai-session-control-group"
              >
                <select
                  className="ai-session-control-select"
                  aria-label={group.ariaLabel}
                  value={selectedValue}
                  disabled={isControlDisabled}
                  title={
                    isModeLocked
                      ? "This screen uses Direct mode only."
                      : isSpeedLocked
                      ? "Speed settings apply to Agent mode only."
                      : undefined
                  }
                  onChange={(event) => onSelect(event.target.value)}
                >
                  {options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      );
    },
    [
      forceDirectMode,
      hasFixedProcessingMode,
      friendlySessionState,
      handleApprovalSelection,
      handleModeSelection,
      handlePlanningSelection,
      handleSpeedSelection,
      operationName
    ]
  );

  const renderReviewList = (title, values) => {
    const items = asTextList(values).slice(0, 6);
    if (!items.length) return null;
    return (
      <div className="ai-review-card__section">
        <span className="ai-review-card__label">{title}</span>
        <ul>
          {items.map((entry, index) => (
            <li key={`${title}-${index}`}>{entry}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderReviewActions = () => {
    if (!activeReviewDraft) return null;
    if (reviewActionInFlight != null) return null;

    const isPreview = activeReviewDraft.kind === REVIEW_TYPE_PREVIEW;
    const canEnablePreviewRefresh = isPreviewRefreshAvailable(activeReviewDraft);
    const actions = isPreview
      ? [
          { action: "acceptpreview", label: "Accept", submitLabel: "Accept Result", tone: "approve" },
          { action: "cancelreview", label: "Reject", submitLabel: "Cancel", tone: "reject" }
        ]
      : [
          ...(planningPreviewEnabled === false
            ? []
            : [{ action: "generatepreview", label: "Preview", submitLabel: "Generate Preview" }]),
          { action: "generateresult", label: "Approve", submitLabel: "Generate Result", tone: "approve" },
          { action: "cancelreview", label: "Reject", submitLabel: "Cancel", tone: "reject" }
        ];

    return (
      <div className="ai-review-card__actions" role="group" aria-label="Review Actions">
        {canEnablePreviewRefresh ? (
          <label className="ai-review-card__refresh-option">
            <input
              type="checkbox"
              checked={enablePreviewRefresh}
              onChange={(event) => setEnablePreviewRefresh(event.target.checked)}
            />
            <span>Enable refresh</span>
          </label>
        ) : null}
        {actions.map((entry) => (
          <button
            type="button"
            key={entry.action}
            className={`ai-review-card__action${entry.tone ? ` ai-review-card__action--${entry.tone}` : ""}`}
            onClick={() => handleReviewAction(entry.action, entry.submitLabel ?? entry.label)}
          >
            {entry.label}
          </button>
        ))}
      </div>
    );
  };

  const renderReviewCard = () => {
    if (!activeReviewDraft) return null;

    const isPreview = activeReviewDraft.kind === REVIEW_TYPE_PREVIEW;
    const title = isPreview ? "Preview Result" : "Planned Result";
    const alternateReviewDraft = isPreview
      ? availableReviewDrafts.plan
      : availableReviewDrafts.preview;
    const reviewToggleLabel = isPreview ? "Plan" : "Preview";
    const previewCommand = isPreview
      ? normalizePreviewUiCommandValue(activeReviewDraft.finalUiCommand)
      : "";
    const previewMessage = isPreview
      ? resolvePreviewPayloadMessage(activeReviewDraft.payload)
      : "";
    const previewValue = isPreview
      ? getObjectPropCI(
          activeReviewDraft.payload,
          "payload",
          "Payload",
          "output",
          "Output",
          "data",
          "Data"
        ) ?? null
      : null;
    const previewHtml =
      isPreview && previewCommand === "render"
        ? sanitizeHtmlFragment(resolvePreviewRenderHtml(previewValue))
        : "";
    const permissionPreviewAdditions =
      isPreview && previewCommand === "update" && isPermissionSetOperation(operationName)
        ? resolvePermissionPreviewAdditions({
            previewValue,
            basePayload: payload,
            reviewContext
          })
        : null;

    return (
      <div className="ai-review-card" role="region" aria-label={title}>
        <div className="ai-review-card__header">
          <div>
            <span className="ai-review-card__eyebrow">{activeReviewDraft.status}</span>
            <h4>{title}</h4>
          </div>
          <div className="ai-review-card__header-actions">
            {alternateReviewDraft ? (
              <button
                type="button"
                className="ai-review-card__toggle"
                data-testid="review-toggle-button"
                onClick={() => setActiveReviewDraft(alternateReviewDraft)}
              >
                {reviewToggleLabel}
              </button>
            ) : activeReviewDraft.finalUiCommand ? (
              <span className="ai-review-card__chip">{activeReviewDraft.finalUiCommand}</span>
            ) : null}
          </div>
        </div>

        {activeReviewDraft.requestedGoal ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Goal</span>
            <p>{activeReviewDraft.requestedGoal}</p>
          </div>
        ) : null}

        {activeReviewDraft.targetPanel ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Target</span>
            <p>{activeReviewDraft.targetPanel}</p>
          </div>
        ) : null}

        {isPreview && previewMessage ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Message</span>
            <p>{previewMessage}</p>
          </div>
        ) : null}

        {isPreview && previewHtml ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Preview</span>
            <div
              className="ai-review-card__preview ai-review-card__preview--render"
              data-testid="preview-review-render"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        ) : null}

        {permissionPreviewAdditions ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Permissions to Add</span>
            {permissionPreviewAdditions.length ? (
              <ul>
                {permissionPreviewAdditions.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            ) : (
              <p>No new permissions will be added.</p>
            )}
          </div>
        ) : null}

        {renderReviewList("Actions", activeReviewDraft.proposedActions)}
        {renderReviewList("Data Needs", activeReviewDraft.dataNeeds)}
        {renderReviewList("Components", activeReviewDraft.components)}
        {renderReviewList("Assumptions", activeReviewDraft.assumptions)}
        {renderReviewList("Open Questions", activeReviewDraft.openQuestions)}

        {!isPreview && activeReviewDraft.expectedOutput ? (
          <div className="ai-review-card__section">
            <span className="ai-review-card__label">Expected Output</span>
            <p>{activeReviewDraft.expectedOutput}</p>
          </div>
        ) : null}
      </div>
    );
  };

  const chatSendButtonLabel = activeReviewDraft ? "Revise" : "Send";

  const renderChatBody = () =>
    forceSingleSessionMode && (uiMode !== "chat" || isLoadingHistory) ? (
      <div
        className="ai-panel-content-right-startup-center ai-chat-content"
        style={{ width: "100%", maxWidth: "none" }}
      >
        <Loading />
      </div>
    ) : uiMode !== "chat" || isLoadingHistory ? (
      <div
        className="ai-panel-content-right-startup-center ai-chat-content"
        style={{ width: "100%", maxWidth: "none" }}
      >
        <h3>{introMessage ?? "Ask your data a question!"}</h3>

        {/* NEW: visible splash text for the user */}
        <p className="ai-panel-splash-message">
          {getSplashText()}
        </p>

        {(sessionChatMessages === undefined || isLoadingHistory) && <Loading />}

        {!forceSingleSessionMode && !hasFixedProcessingMode && sessionChatMessages !== undefined && !isLoadingHistory ? (
          <FieldControl
            disabled={false}
            fieldMetadata={{
              FieldType: SystemTypes.UIFieldType.SearchSelectList,
              DataJson: jsonData,
              PlaceHolder: "Start a new conversation..."
            }}
            value={selectedSessionId ?? null}
            onChange={(value, item) => handleSelectSession(value, item)}
          />
        ) : null}

        {renderSessionControlBar("ai-session-control-bar--startup")}

        {!forceSingleSessionMode && sessionChatMessages !== undefined && !isLoadingHistory && (
          <ButtonControl type={"ok"} disabled={operationName ? false : true} onClick={handleStartChat}>
            Start
          </ButtonControl>
        )}
      </div>
    ) : (
      <div className="ai-chat-shell">
        <div className="ai-chat-shell__header">
          <div className="ai-chat-top-controls">
            {!forceSingleSessionMode ? (
              <button
                type="button"
                className="ai-chat-back-button"
                onClick={handleBackToSessionPicker}
                title="Sessions"
                aria-label="Sessions"
              >
                {"<< Sessions"}
              </button>
            ) : null}
            {activeSessionSummary ? (
              <span className="ai-chat-top-controls__latest-turn-label">Latest Turn</span>
            ) : null}
            {activeSessionSummary ? (
              <button
                type="button"
                className="ai-chat-top-controls__toggle"
                aria-expanded={!isLatestTurnCollapsed}
                aria-label={isLatestTurnCollapsed ? "Expand latest turn details" : "Collapse latest turn details"}
                title={isLatestTurnCollapsed ? "Show latest turn details" : "Hide latest turn details"}
                onClick={() => setIsLatestTurnCollapsed((value) => !value)}
              >
                {isLatestTurnCollapsed ? "▾" : "▴"}
              </button>
            ) : null}
            <div className="ai-chat-mode-summary">
              <span className="ai-chat-mode-summary__chip">{friendlySessionState.mode}</span>
              <span className="ai-chat-mode-summary__chip">{friendlySessionState.approval}</span>
              <span className="ai-chat-mode-summary__chip">{friendlySessionState.speed}</span>
            </div>
          </div>
          {activeSessionSummary && !isLatestTurnCollapsed ? (
            <div className="ai-chat-status-strip">
              <div className="ai-chat-status-strip__summary">
                <div className="ai-chat-status-strip__summary-main">
                  <span className="ai-chat-status-strip__label">Latest Turn</span>
                  <div className="ai-chat-status-strip__chips" aria-label="Orchestration status">
                    {activeSessionSummary?.ApprovalStatus ? (
                      <span className="ai-chat-status-strip__chip">
                        {humanizeStatusToken(activeSessionSummary.ApprovalStatus)}
                      </span>
                    ) : null}
                    {shouldShowTerminalReason(activeSessionSummary?.TerminalReason) ? (
                      <span className="ai-chat-status-strip__chip">
                        {humanizeTerminalReason(activeSessionSummary?.TerminalReason, activeSessionSummary?.AIOutcomeStatus)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="ai-chat-status-strip__grid">
                <div className="ai-chat-status-strip__metric">
                  <span>Status</span>
                  <strong>{humanizeStatusToken(activeSessionSummary?.AIOutcomeStatus || "Ready")}</strong>
                </div>
                <div className="ai-chat-status-strip__metric">
                  <span>Approval</span>
                  <strong>{humanizeStatusToken(activeSessionSummary?.ApprovalStatus || "AutoSafe")}</strong>
                </div>
                <div className="ai-chat-status-strip__metric">
                  <span>Terminal</span>
                  <strong>{humanizeTerminalReason(activeSessionSummary?.TerminalReason, activeSessionSummary?.AIOutcomeStatus)}</strong>
                </div>
                <div className="ai-chat-status-strip__metric">
                  <span>Tools</span>
                  <strong>{Number(activeSessionSummary?.ToolCallCount ?? 0)}</strong>
                </div>
                <div className="ai-chat-status-strip__metric">
                  <span>Steps</span>
                  <strong>{Number(activeSessionSummary?.StepCount ?? 0)}</strong>
                </div>
              </div>

              {isApprovalPending(activeSessionSummary?.ApprovalStatus) ? (
                <div className="ai-chat-status-strip__notice ai-chat-status-strip__notice--approval">
                  <div className="ai-chat-status-strip__notice-copy">
                    <span>Approval needed before the protected action can run.</span>
                    <div className="ai-chat-status-strip__actions">
                      <button
                        type="button"
                        className="ai-chat-status-strip__action ai-chat-status-strip__action--approve"
                        onClick={() => handleApprovalAction("approve")}
                        disabled={approvalActionInFlight != null}
                      >
                        {approvalActionInFlight === "approve" ? "Approving..." : "Approve"}
                      </button>
                      <button
                        type="button"
                        className="ai-chat-status-strip__action ai-chat-status-strip__action--deny"
                        onClick={() => handleApprovalAction("deny")}
                        disabled={approvalActionInFlight != null}
                      >
                        {approvalActionInFlight === "deny" ? "Denying..." : "Deny"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {hasTerminalReason(activeSessionSummary?.TerminalReason) ? (
                <div className="ai-chat-status-strip__notice ai-chat-status-strip__notice--terminal">
                  Turn ended with {humanizeTerminalReason(activeSessionSummary?.TerminalReason, activeSessionSummary?.AIOutcomeStatus)}.
                </div>
              ) : null}

              {recentSessionChats.length > 0 ? (
                <div className="ai-chat-activity" aria-label="Recent session activity">
                  {recentSessionChats.map((chat) => (
                    <div className="ai-chat-activity__item" key={chat?._id || chat?.Sequence}>
                      <span className="ai-chat-activity__turn">Turn {chat?.Sequence ?? 0}</span>
                      <span>{humanizeStatusToken(chat?.AIOutcomeStatus || "Ready")}</span>
                      <span>{humanizeStatusToken(chat?.ApprovalStatus || "AutoSafe")}</span>
                      <span>Tools {Number(chat?.ToolCallCount ?? 0)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="ai-chat-shell__body">
          <AIChatControl
            // Only changes when the user explicitly selects a session, not when a new session is saved mid-send.
            key={chatInstanceKey}
            ref={chatRef}
            placeholder={
              operationName
                ? getSplashText() // use splash text as chat placeholder
                : "AI chat currently not supported"
            }
            onInit={handleInit}
            onSend={handleSend}
            onSave={handleSave}
            thinking={thinking}
            disabled={operationName ? false : true}
            onOpenDetails={handleOpenDetails}
            onOpenMessageDetails={handleOpenMessageDetails}
            getAssociatedOutputTarget={getAssociatedOutputTarget}
            onOpenAssociatedOutput={handleOpenAssociatedOutput}
            scrollContent={renderReviewCard()}
            reviewFooterContent={renderReviewActions()}
            footerContent={renderSessionControlBar("ai-session-control-bar--live")}
            sendButtonLabel={chatSendButtonLabel}
          />
        </div>
      </div>
    );

  // ---------- Pane builders ----------
  const withAiTab = (tabs, defaultKey = "ai") => {
    const aiIdx = tabs.findIndex(
      (t) =>
        t.key?.toLowerCase?.() === "ai" ||
        String(t.title || t.label || "").toLowerCase() === "ai"
    );

    const aiTab = {
      key: "ai",
      title: "AI",
      content: (
        <div
          className="ai-panel-right-stretch"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            flex: "1 1 auto"
          }}
        >
          {renderChatBody()}
        </div>
      )
    };

    if (aiIdx >= 0) {
      const clone = tabs.slice();
      clone[aiIdx] = { ...clone[aiIdx], ...aiTab };
      return { tabs: clone, defaultKey };
    }
    return { tabs: [aiTab, ...tabs], defaultKey };
  };

  const buildPane = (side, input) => {
    const isChatHere = isChatAiEnabled && chatPane === side;

    let reactNode = null;
    let tabsArray = null;

    if (isTabsArray(input)) {
      tabsArray = input;
    } else if (input != null) {
      reactNode = input;
    }

    if (side === "left" && !tabsArray) {
      const manualLeft = (
        <>
          {!dragging || !enableContentDragHiding ? (
            children
          ) : (
            <DragOverlay>Resizing...</DragOverlay>
          )}
          {reactNode}
        </>
      );
      reactNode = manualLeft;
    }

    if (isChatHere) {
      if (tabsArray) {
        const { tabs, defaultKey } = withAiTab(tabsArray, "ai");
        return (
          <TabPaneControl
            className="ai-panel-tabs"
            tabs={tabs}
            defaultKey={defaultKey}
          />
        );
      }

      const chat = (
        <div
          className="ai-panel-right-stretch"
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "stretch",
            flex: "1 1 auto"
          }}
        >
          {renderChatBody()}
        </div>
      );

      if (!reactNode) return chat;

      return (
        <TabPaneControl
          className="ai-panel-tabs"
          tabs={[
            { key: "ai", title: "AI", content: chat },
            { key: "manual", title: "Manual", content: reactNode }
          ]}
          defaultKey="ai"
        />
      );
    }

    if (tabsArray) {
      return (
        <TabPaneControl className="ai-panel-tabs" tabs={tabsArray} />
      );
    }
    return reactNode ?? null;
  };

  const leftPane = buildPane("left", leftInput);
  const middlePane = buildPane("middle", middleInput);
  const rightPane = buildPane("right", rightInput);
  const paneCount =
    (leftPane ? 1 : 0) +
    (rightPane ? 1 : 0) +
    (middlePane ? 1 : 0);
  const hasMiddlePane = !!middlePane;
  const resolveMobilePaneLabel = (side, input) => {
    if (isChatAiEnabled && chatPane === side) return "AI";

    if (isTabsArray(input) && input.length === 1) {
      return input[0]?.title ?? input[0]?.label ?? null;
    }

    return { left: "Content", middle: "Details", right: "Output" }[side];
  };
  const paneLabels = {
    left: resolveMobilePaneLabel("left", leftInput),
    middle: resolveMobilePaneLabel("middle", middleInput),
    right: resolveMobilePaneLabel("right", rightInput),
  };
  const supportsChatPaneCollapse =
    isChatAiEnabled &&
    chatPane === "right" &&
    effectiveChatPaneCollapseMode !== CHAT_PANE_COLLAPSE_DISABLED &&
    !!rightPane;
  const canCollapseChatPane = supportsChatPaneCollapse && chatPaneToggleEnabled;
  const chatPaneToggleRight = !supportsChatPaneCollapse
    ? "0px"
    : isChatPaneCollapsed
      ? "0px"
      : `${Math.max(
          0,
          Number(splitPaneSize?.right ?? 0) || Number(minRight ?? 0)
        )}px`;
  const toggleChatPaneCollapsed = useCallback(() => {
    if (!supportsChatPaneCollapse || !chatPaneToggleEnabled) return;
    setIsChatPaneCollapsed((prev) => !prev);
  }, [chatPaneToggleEnabled, supportsChatPaneCollapse]);
  const handleChatPaneToggleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      toggleChatPaneCollapsed();
    },
    [toggleChatPaneCollapsed]
  );

  return (
    <>
      {toasterMessage && (
        <ToastNotifications position={"top"} message={toasterMessage} />
      )}

      <DialogControl
        openDialog={showChatDialog}
        onX={() => {
          setShowChatDialog(false);
          setSelectedChat(null);
          setSelectedChatDefaultTab("flow");
          setLocalErrorDetail(null);
        }}
        hideButtonRow={true}
        canFullScreen={false}
        canOpenNewWindow={false}
        title={"Chat Details"}
        width={1000}
        subTitle={null}
      >
        <div>
          {selectedChat ? (
            <AIChatDetailsControl
              aiChat={selectedChat}
              defaultTab={selectedChatDefaultTab}
            />
          ) : localErrorDetail ? (
            <div className="ai-chat-details">
              <div className="ai-tabs">
                <button className="ai-tab active" type="button">Information</button>
              </div>
              <div className="ai-grid" style={{ gridTemplateColumns: "1fr" }}>
                <div className="ai-col">
                  <div className="ai-details" style={{ gridTemplateColumns: "repeat(12, 1fr)" }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div className="ai-section">
                        <h4>Error Message</h4>
                        <div className="ai-pre">{localErrorDetail.message}</div>
                      </div>
                    </div>
                    {localErrorDetail.debugErrorMessage ? (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="ai-section">
                          <h4>Debug Error</h4>
                          <div className="ai-pre">{localErrorDetail.debugErrorMessage}</div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Loading />
          )}
        </div>
      </DialogControl>

      <div
        className={[
          "ai-panel-content",
          className,
          paneCount === 1 ? "is-single-pane" : null,
          supportsChatPaneCollapse ? "can-collapse-chat-pane" : null,
          supportsChatPaneCollapse && isChatPaneCollapsed ? "is-chat-pane-collapsed" : null,
          supportsChatPaneCollapse && hasMiddlePane ? "has-middle-pane" : null,
          supportsChatPaneCollapse && !hasMiddlePane ? "no-middle-pane" : null,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <SplitPaneControl
          panes={paneCount}
          storageKey={storageKey}
          initialRightPct={initialRightPct ?? 0.33}
          initialLeftPct={initialLeftPct ?? 0.33}
          minLeft={minLeft ?? 360}
          minRight={minRight ?? 360}
          minMiddle={minMiddle ?? 360}
          onResize={handleSplitPaneResize}
          onDragStart={() => setDraging(true)}
          onDragEnd={() => setDraging(false)}
          left={leftPane}
          middle={middlePane || undefined}
          right={rightPane}
          paneLabels={paneLabels}
        />
        {canCollapseChatPane && (
          <button
            type="button"
            className={`ai-chat-pane-toggle${isChatPaneCollapsed ? " is-collapsed" : " is-open"}`}
            onDoubleClick={toggleChatPaneCollapsed}
            onKeyDown={handleChatPaneToggleKeyDown}
            aria-label={`${isChatPaneCollapsed ? "Open" : "Collapse"} AI pane`}
            title={`Double-click to ${isChatPaneCollapsed ? "open" : "collapse"} the AI pane`}
            style={{ right: chatPaneToggleRight }}
          >
            <span
              aria-hidden
              className={`ai-chat-pane-toggle__icon ${
                isChatPaneCollapsed ? "ai-chat-pane-toggle__icon--left" : "ai-chat-pane-toggle__icon--right"
              }`}
            />
          </button>
        )}
        {bottomDrawerEnabled && (
          <>
            <button
              type="button"
              className={`ai-bottom-drawer-toggle-icon ${isBottomDrawerOpen ? "open" : ""}`}
              onClick={() => setIsBottomDrawerOpen((prev) => !prev)}
              aria-label={`${isBottomDrawerOpen ? "Hide" : "Show"} ${bottomDrawerTitle}`}
              title={`${isBottomDrawerOpen ? "Hide" : "Show"} ${bottomDrawerTitle}`}
            >
              <span aria-hidden>🐞</span>
            </button>
            <div className={`ai-bottom-drawer ${isBottomDrawerOpen ? "open" : ""}`}>
              {isBottomDrawerOpen && (
                <div className="ai-bottom-drawer__header">
                  <span>{bottomDrawerTitle}</span>
                  <button
                    type="button"
                    className="ai-bottom-drawer__close"
                    onClick={() => setIsBottomDrawerOpen(false)}
                    aria-label={`Close ${bottomDrawerTitle}`}
                  >
                    X
                  </button>
                </div>
              )}
              <div className="ai-bottom-drawer__content">
                {bottomDrawerContent}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const humanizeStatusToken = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "None";
  if (text.toLowerCase() === "returnedchat") return "Succeeded";
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
};

const humanizeTerminalReason = (reason, outcomeStatus) => {
  const normalized = String(reason ?? "").trim().toLowerCase();
  if (normalized === "returnedchat") {
    return humanizeStatusToken(outcomeStatus || "Succeeded");
  }
  if (normalized === "" || normalized === "none") {
    return "Active";
  }
  return humanizeStatusToken(reason);
};

const isApprovalPending = (status) =>
  String(status ?? "").trim().toLowerCase() === "pendingapproval";

const shouldShowTerminalReason = (reason) => {
  const normalized = String(reason ?? "").trim().toLowerCase();
  return normalized !== "" && normalized !== "none" && normalized !== "active" && normalized !== "returnedchat";
};

const hasTerminalReason = (reason) => {
  const normalized = String(reason ?? "").trim().toLowerCase();
  return normalized !== "" && normalized !== "none" && normalized !== "active" && normalized !== "returnedchat";
};
